/**
 * Capacitor Device Management Service
 * 
 * Handles device registration, FCM token management, and push notifications
 * for the Next.js Capacitor mobile app.
 * 
 * This service is the equivalent of vue-app's deviceManagementService
 * and messagingService for push notifications.
 * 
 * @author TotalAccess Development Team
 * @date November 27, 2025
 */

'use client';

import { Capacitor } from '@capacitor/core';
import { isSilentFetchError } from '@/lib/capacitorFetchPatch';
import { storePendingEnrollment } from '@/lib/enrollmentPrefill';

// Dynamic imports for Capacitor plugins (only on native)
type GenericRecord = Record<string, unknown>;

interface DevicePluginInfo {
  platform: string;
  model: string;
  manufacturer: string;
  osVersion: string;
  isVirtual: boolean;
}

interface DevicePluginId {
  identifier: string;
}

interface DevicePluginLike {
  getInfo(): Promise<DevicePluginInfo>;
  getId(): Promise<DevicePluginId>;
}

interface PushPermissionResult {
  receive: string;
}

interface NativePushNotificationPayload extends GenericRecord {
  type?: string;
  action?: string;
  thread_id?: string;
  thread_name?: string;
  sender_name?: string;
  call_id?: string;
  caller_name?: string;
  call_type?: string;
  is_sip_call?: boolean | string;
  sip_trunk_name?: string;
  sip_did_number?: string;
  sip_data?: GenericRecord;
  message_id?: string;
  folder?: string;
  account_id?: string | number;
  accountId?: string | number;
  mailbox_id?: string | number;
  mailboxId?: string | number;
  client_id?: string | number;
  clientId?: string | number;
  appointment_id?: string;
  parent_job_id?: string;
  challenge_id?: string;
  purpose?: string;
  approval_url?: string;
  mobile_approval_url?: string;
  approval_token?: string;
  expires_at?: string | number;
  title?: string;
  body?: string;
}

interface NativePushNotificationSchema {
  title?: string;
  body?: string;
  data?: NativePushNotificationPayload;
  notification?: {
    title?: string;
    body?: string;
    data?: NativePushNotificationPayload;
  };
}

interface NativePushActionPerformed {
  notification?: {
    title?: string;
    body?: string;
    data?: NativePushNotificationPayload;
  };
}

interface PushNotificationsPluginLike {
  checkPermissions(): Promise<PushPermissionResult>;
  requestPermissions(): Promise<PushPermissionResult>;
  register(): Promise<void>;
  addListener(eventName: 'registration', callback: (token: PushNotificationToken) => void): Promise<unknown> | unknown;
  addListener(eventName: 'registrationError', callback: (error: unknown) => void): Promise<unknown> | unknown;
  addListener(eventName: 'pushNotificationReceived', callback: (notification: NativePushNotificationSchema) => void | Promise<void>): Promise<unknown> | unknown;
  addListener(eventName: 'pushNotificationActionPerformed', callback: (notification: NativePushActionPerformed) => void): Promise<unknown> | unknown;
}

interface PersistedDeepLinkPayload extends GenericRecord {
  timestamp?: number;
  threadId?: string;
  threadName?: string;
  messageId?: string;
  folder?: string;
  appointmentId?: string;
  parentJobId?: string;
  action?: string;
  /** App-internal path a meeting deep link resolved to (e.g. /meeting/join?token=…). */
  meetingUrl?: string;
}

let Device: DevicePluginLike | null = null;
let PushNotifications: PushNotificationsPluginLike | null = null;

function asNotificationPayload(value: unknown): NativePushNotificationPayload {
  return typeof value === 'object' && value !== null
    ? (value as NativePushNotificationPayload)
    : {};
}

function isMissingPreferencesKeyError(error: unknown): boolean {
  const message =
    typeof error === 'string'
      ? error
      : typeof error === 'object' && error !== null && 'message' in error
        ? String((error as { message?: unknown }).message || '')
        : '';

  return message.includes('Item with given key does not exist');
}

export interface DeviceInfo {
  deviceId: string;           // Unique device identifier (UUID from Capacitor)
  appId: string;              // App installation ID (persists across sessions)
  platform: 'android' | 'ios' | 'web';
  model: string;
  manufacturer: string;
  osVersion: string;
  isVirtual: boolean;
  deviceName: string;
}

export interface PushNotificationToken {
  value: string;
}

export interface PushNotification {
  title?: string;
  body?: string;
  data?: NativePushNotificationPayload;
}

export type PushPermissionStatus = 'granted' | 'denied' | 'prompt' | 'unavailable';

class CapacitorDeviceService {
  private deviceInfo: DeviceInfo | null = null;
  private fcmToken: string | null = null;
  private initialized: boolean = false;
  private initializationPromise: Promise<boolean> | null = null; // Async-safe initialization lock
  private pushListenersRegistered: boolean = false;
  private notificationHandlers: Set<(notification: PushNotification) => void> = new Set();
  private tokenHandlers: Set<(token: string | null) => void> = new Set();
  
  // Track notification IDs by thread_id so we can cancel them when messages are read
  private activeNotificationIds: Map<string, number> = new Map();

  /**
   * Initialize the device service
   * Should be called after user login
   * 
   * NOTE: This method is async-safe - multiple concurrent calls will share
   * the same initialization promise to prevent duplicate initialization.
   */
  async initialize(): Promise<boolean> {
    // Fast path: already initialized
    if (this.initialized) return true;
    
    // Async-safe: if initialization is in progress, wait for it
    if (this.initializationPromise) {
      return this.initializationPromise;
    }
    
    if (!this.isNative()) {
      console.log('[DeviceService] Not running in native app');
      return false;
    }

    // Start initialization and store the promise
    this.initializationPromise = this._doInitialize();
    
    try {
      return await this.initializationPromise;
    } finally {
      // Clear the promise after completion (success or failure)
      // This allows retry if initialization failed
      if (!this.initialized) {
        this.initializationPromise = null;
      }
    }
  }

  /**
   * Internal initialization logic
   */
  private async _doInitialize(): Promise<boolean> {
    try {
      // Dynamic import Capacitor plugins - with extra safety
      try {
        const { Device: DevicePlugin } = await import('@capacitor/device');
        Device = DevicePlugin;
      } catch (deviceError) {
        console.warn('[DeviceService] Device plugin not available:', deviceError);
        // Continue without device plugin - use fallbacks
      }

      // Try to import Push Notifications (may fail in some environments)
      try {
        const pushModule = await import('@capacitor/push-notifications');
        PushNotifications = pushModule.PushNotifications;
      } catch (pushError) {
        console.warn('[DeviceService] Push notifications not available:', pushError);
        // Continue without push - we'll try again when requesting permissions
      }

      // Get device info (only if Device plugin loaded)
      if (Device) {
        await this.loadDeviceInfo();
        // §29: durable install UUID — iOS Keychain persists it across
        // reinstalls so the server can reconcile the install's devices row.
        await this.loadInstallUuid();
      } else {
        // Create fallback device info - use stable identifiers
        const fallbackPlatform = Capacitor.getPlatform();
        const fallbackDeviceId = (typeof localStorage !== 'undefined' && localStorage.getItem('capacitor_device_id')) || `fallback_${fallbackPlatform}`;
        const fallbackAppId = (typeof localStorage !== 'undefined' && localStorage.getItem('capacitor_app_id')) || `capacitor_${fallbackPlatform}_${fallbackDeviceId}`;
        this.deviceInfo = {
          deviceId: fallbackDeviceId,
          appId: fallbackAppId,
          platform: fallbackPlatform as 'android' | 'ios' | 'web',
          model: 'Unknown',
          manufacturer: 'Unknown',
          osVersion: 'Unknown',
          isVirtual: false,
          deviceName: `${fallbackPlatform} device`
        };
        console.log('[DeviceService] Using fallback device info');
      }

      this.initialized = true;
      // §29: drain queued install commands (wipe_client_data etc.) — the
      // pull channel is authoritative when FCM can't reach a closed app.
      void this.drainPendingInstallCommands();
      return true;
    } catch (error) {
      console.error('[DeviceService] ❌ Failed to initialize:', error);
      // Still mark as initialized to prevent repeated failures
      this.initialized = true;
      return false;
    }
  }

  /**
   * Drain server-queued install commands for this install. Best-effort —
   * only runs when the user is authenticated.
   */
  async drainPendingInstallCommands(): Promise<void> {
    try {
      const { authService } = await import('./authService');
      if (!authService.isAuthenticated()) return;
      const { drainAndProcessInstallCommands } = await import('./installCommands');
      await drainAndProcessInstallCommands();
    } catch (err) {
      console.warn('[DeviceService] Install command drain failed:', err);
    }
  }

  /**
   * Check if running in Capacitor native app
   */
  isNative(): boolean {
    return Capacitor.isNativePlatform();
  }

  /**
   * Get platform (android, ios, web)
   */
  getPlatform(): 'android' | 'ios' | 'web' {
    const platform = Capacitor.getPlatform();
    if (platform === 'android') return 'android';
    if (platform === 'ios') return 'ios';
    return 'web';
  }

  /**
   * Detect push provider based on build channel.
   * Huawei builds use HMS Push Kit, Play builds use FCM.
   */
  getPushProvider(): 'fcm' | 'hms' {
    if (typeof navigator !== 'undefined') {
      const ua = navigator.userAgent || '';
      if (/TotalAccessHuawei/i.test(ua)) {
        return 'hms';
      }
    }
    return 'fcm';
  }

  /**
   * Load device information from Capacitor
   */
  private async loadDeviceInfo(): Promise<void> {
    if (!Device) return;

    try {
      const [info, id] = await Promise.all([
        Device.getInfo(),
        Device.getId()
      ]);

      // Try to restore app_id from Preferences first (survives localStorage clears)
      await this.restoreAppIdFromPreferences();

      // Generate app_id from device UUID + platform (will use stored if available)
      const appId = this.generateAppId(id.identifier, info.platform);

      this.deviceInfo = {
        deviceId: id.identifier,
        appId: appId,
        platform: info.platform as 'android' | 'ios' | 'web',
        model: info.model,
        manufacturer: info.manufacturer,
        osVersion: info.osVersion,
        isVirtual: info.isVirtual,
        deviceName: `${info.manufacturer} ${info.model}`
      };

      // Store app_id in localStorage for persistence
      if (typeof localStorage !== 'undefined') {
        localStorage.setItem('capacitor_app_id', appId);
        localStorage.setItem('capacitor_device_id', id.identifier);
      }

    } catch (error) {
      if (!isSilentFetchError(error)) console.error('[DeviceService] Failed to load device info:', error);
    }
  }

  /**
   * Generate a consistent app_id from device UUID
   * CRITICAL: Also stores in Capacitor Preferences to survive localStorage wipes
   */
  private generateAppId(deviceUuid: string, platform: string): string {
    // Check if we have a stored app_id in localStorage
    if (typeof localStorage !== 'undefined') {
      const storedAppId = localStorage.getItem('capacitor_app_id');
      if (storedAppId) {
        // Also persist to Preferences asynchronously for redundancy
        this.persistAppIdToPreferences(storedAppId);
        return storedAppId;
      }
    }

    // Generate deterministic app_id from platform + device UUID (no timestamp)
    // This ensures the same device always produces the same app_id
    const newAppId = `${platform}_${deviceUuid}`;
    
    // Persist to Preferences immediately
    this.persistAppIdToPreferences(newAppId);
    
    return newAppId;
  }

  /**
   * Persist app_id to Capacitor Preferences (native SharedPreferences)
   * This survives Android clearing WebView localStorage
   */
  private async persistAppIdToPreferences(appId: string): Promise<void> {
    if (!Capacitor.isNativePlatform()) return;
    try {
      const { Preferences } = await import('@capacitor/preferences');
      await Preferences.set({ key: 'capacitor_app_id', value: appId });
      console.log('[DeviceService] 📱 app_id persisted to Preferences:', appId.substring(0, 20) + '...');
    } catch (error) {
      console.warn('[DeviceService] Failed to persist app_id to Preferences:', error);
    }
  }

  /**
   * Restore app_id from Capacitor Preferences if localStorage was cleared
   * MUST be called before generateAppId to prevent unnecessary new app_id generation
   */
  async restoreAppIdFromPreferences(): Promise<string | null> {
    if (!Capacitor.isNativePlatform()) return null;
    try {
      const { Preferences } = await import('@capacitor/preferences');
      const result = await Preferences.get({ key: 'capacitor_app_id' });
      if (result?.value) {
        // Restore to localStorage for sync access
        if (typeof localStorage !== 'undefined') {
          localStorage.setItem('capacitor_app_id', result.value);
        }
        console.log('[DeviceService] 📱 app_id restored from Preferences:', result.value.substring(0, 20) + '...');
        return result.value;
      }
    } catch (error) {
      if (!isMissingPreferencesKeyError(error)) {
        console.warn('[DeviceService] Failed to restore app_id from Preferences:', error);
      }
    }
    return null;
  }

  /**
   * Get current device info
   */
  getDeviceInfo(): DeviceInfo | null {
    return this.deviceInfo;
  }

  /**
   * Get current push permission status
   */
  async getPushPermissionStatus(): Promise<PushPermissionStatus> {
    if (!this.isNative()) {
      return 'unavailable';
    }

    if (!PushNotifications) {
      try {
        const pushModule = await import('@capacitor/push-notifications');
        PushNotifications = pushModule.PushNotifications;
      } catch (error) {
        console.warn('[DeviceService] Push notifications module unavailable for permission check:', error);
        return 'unavailable';
      }
    }

    try {
      const status = await PushNotifications.checkPermissions();
      if (status.receive === 'granted' || status.receive === 'denied' || status.receive === 'prompt') {
        return status.receive;
      }
      return 'unavailable';
    } catch (error) {
      console.warn('[DeviceService] Failed to check push permissions:', error);
      return 'unavailable';
    }
  }

  /**
   * Get app_id for authentication headers
   */
  getAppId(): string | null {
    if (this.deviceInfo?.appId) {
      return this.deviceInfo.appId;
    }
    if (typeof localStorage !== 'undefined') {
      return localStorage.getItem('capacitor_app_id');
    }
    return null;
  }

  /**
   * §29: load/persist the stable install UUID. On iOS the native
   * InstallIdentity plugin stores it in the Keychain (survives uninstall —
   * identifierForVendor does not when it's the last vendor app). On other
   * platforms it falls back to Preferences/localStorage.
   */
  private installUuid: string | null = null;

  private async loadInstallUuid(): Promise<string | null> {
    const stored = typeof localStorage !== 'undefined' ? localStorage.getItem('totalaccess_install_uuid') : null;
    if (stored) {
      this.installUuid = stored;
    }
    if (Capacitor.getPlatform() === 'ios') {
      try {
        const { registerPlugin } = await import('@capacitor/core');
        const InstallIdentity = registerPlugin<{ getOrCreateInstallUuid(): Promise<{ install_uuid: string }> }>('InstallIdentity');
        const result = await InstallIdentity.getOrCreateInstallUuid();
        if (result?.install_uuid) {
          this.installUuid = result.install_uuid;
        }
      } catch (err) {
        console.warn('[DeviceService] InstallIdentity plugin unavailable:', err);
      }
    }
    if (!this.installUuid) {
      try {
        const { Preferences } = await import('@capacitor/preferences');
        const existing = await Preferences.get({ key: 'totalaccess_install_uuid' });
        if (existing?.value) {
          this.installUuid = existing.value;
        } else {
          const created = crypto.randomUUID();
          await Preferences.set({ key: 'totalaccess_install_uuid', value: created });
          this.installUuid = created;
        }
      } catch {
        // Non-critical
      }
    }
    if (this.installUuid && typeof localStorage !== 'undefined') {
      localStorage.setItem('totalaccess_install_uuid', this.installUuid);
    }
    return this.installUuid;
  }

  getInstallUuid(): string | null {
    if (this.installUuid) return this.installUuid;
    if (typeof localStorage !== 'undefined') {
      return localStorage.getItem('totalaccess_install_uuid');
    }
    return null;
  }

  /**
   * Get device_id
   */
  getDeviceId(): string | null {
    if (this.deviceInfo?.deviceId) {
      return this.deviceInfo.deviceId;
    }
    if (typeof localStorage !== 'undefined') {
      return localStorage.getItem('capacitor_device_id');
    }
    return null;
  }

  /**
   * Request push notification permissions and get FCM token
   */
  async requestPushNotificationPermission(): Promise<string | null> {
    if (!this.isNative()) {
      console.log('[DeviceService] Push notifications not available - not native');
      return null;
    }

    // Huawei builds use HMS Push Kit, not Firebase. The @capacitor/push-notifications
    // plugin calls FirebaseMessaging.getInstance() which crashes with
    // "Default FirebaseApp is not initialized" because Huawei builds have no
    // google-services.json. Use the native PushToken plugin instead, which
    // delegates to HuaweiPushProvider (HMS Push Kit) on Huawei builds.
    if (this.getPushProvider() === 'hms') {
      console.log('[DeviceService] Huawei build detected — using HMS Push Kit via PushToken plugin');
      try {
        const { PushToken } = await import('@/plugins/PushToken');
        const { token, provider } = await PushToken.getToken();
        if (token) {
          console.log(`[DeviceService] ✅ HMS Push token received (provider: ${provider})`);
          this.fcmToken = token;
          const userId = this.getCurrentUserId();
          if (userId) {
            this.registerFCMTokenWithServer(userId).catch(err => {
              console.error('[DeviceService] Error registering HMS token:', err);
            });
          }
          if (typeof localStorage !== 'undefined') {
            localStorage.setItem('capacitor_fcm_token', token);
          }
          return token;
        }
        console.warn('[DeviceService] HMS Push token was empty');
        return null;
      } catch (err) {
        console.error('[DeviceService] Failed to get HMS Push token:', err);
        return null;
      }
    }

    // If we already have a token in memory, ensure it's registered with server
    if (this.fcmToken) {
      console.log('[DeviceService] Using cached FCM token, ensuring server registration');
      // Ensure token is registered with server (may have been cached but not sent)
      const userId = this.getCurrentUserId();
      console.log('[DeviceService] getCurrentUserId returned:', userId);
      if (userId) {
        this.registerFCMTokenWithServer(userId).catch(err => {
          console.error('[DeviceService] Error ensuring token registration:', err);
        });
      } else {
        console.warn('[DeviceService] No userId available, cannot register cached token');
      }
      return this.fcmToken;
    }

    // Check localStorage for existing token
    const cachedToken = typeof localStorage !== 'undefined' 
      ? localStorage.getItem('capacitor_fcm_token') 
      : null;
    if (cachedToken) {
      console.log('[DeviceService] Using localStorage FCM token:', cachedToken.substring(0, 20) + '...');
      this.fcmToken = cachedToken;
      // Ensure token is registered with server (may have been cached but not sent)
      const userId = this.getCurrentUserId();
      console.log('[DeviceService] getCurrentUserId returned:', userId);
      if (userId) {
        this.registerFCMTokenWithServer(userId).catch(err => {
          console.error('[DeviceService] Error ensuring token registration:', err);
        });
      } else {
        console.warn('[DeviceService] No userId available, cannot register localStorage token');
      }
      return cachedToken;
    }

    // Try to load push notifications if not already loaded
    if (!PushNotifications) {
      try {
        console.log('[DeviceService] Loading push notifications module...');
        const pushModule = await import('@capacitor/push-notifications');
        PushNotifications = pushModule.PushNotifications;
        console.log('[DeviceService] ✅ Push notifications module loaded on demand');
      } catch (error) {
        if (!isSilentFetchError(error)) console.error('[DeviceService] ❌ Failed to load push notifications:', error);
        return null;
      }
    }

    try {
      // Set up listeners BEFORE registering to ensure we catch the token
      this.setupPushListeners();

      // Check current permission status
      let permStatus = await PushNotifications.checkPermissions();
      console.log('[DeviceService] Push permission status:', permStatus.receive);

      if (permStatus.receive === 'prompt') {
        // Request permission
        console.log('[DeviceService] Requesting push notification permission...');
        permStatus = await PushNotifications.requestPermissions();
        console.log('[DeviceService] Permission result:', permStatus.receive);
      }

      if (permStatus.receive !== 'granted') {
        console.warn('[DeviceService] Push notification permission denied');
        return null;
      }

      // Register for push notifications - FIRE AND FORGET
      // Don't await this - on Android, awaiting can cause issues during app state transitions
      console.log('[DeviceService] Calling PushNotifications.register()...');
      const pushNotifications = PushNotifications;
      if (!pushNotifications) {
        return null;
      }
      
      // Use setTimeout to defer the register call slightly
      // This allows the app to stabilize after the permission dialog closes
      setTimeout(() => {
        pushNotifications.register()
          .then(() => {
            console.log('[DeviceService] 📱 Push notification registration completed');
          })
          .catch((regError: unknown) => {
            console.error('[DeviceService] Register() error:', regError);
          });
      }, 100);

      console.log('[DeviceService] 📱 Push notification registration scheduled');

      // Return null - the token will arrive via the 'registration' listener
      // and be stored in localStorage + auto-registered with server
      return null;
    } catch (error) {
      console.error('[DeviceService] ❌ Failed to request push permissions:', error);
      return null;
    }
  }

  /**
   * Set up push notification listeners
   */
  private setupPushListeners(): void {
    if (this.pushListenersRegistered || !PushNotifications) return;
    const pushNotifications = PushNotifications;

    // Registration success - get the FCM token
    pushNotifications.addListener('registration', (token: PushNotificationToken) => {
      try {
        console.log('[DeviceService] ✅ FCM Token received:', token.value.substring(0, 20) + '...');
        this.setFcmToken(token.value);

        // Defer auto-registration to let app stabilize after permission dialog
        setTimeout(() => {
          this.autoRegisterFCMToken().catch(err => {
            console.error('[DeviceService] Auto-register error:', err);
          });
        }, 1000);
      } catch (error) {
        console.error('[DeviceService] Error in registration handler:', error);
      }
    });

    // Registration error
    pushNotifications.addListener('registrationError', (error: unknown) => {
      console.error('[DeviceService] ❌ FCM Registration error:', error);
    });

    // Notification received while app is in foreground
    pushNotifications.addListener('pushNotificationReceived', async (notification: NativePushNotificationSchema) => {
      console.log('[DeviceService] 📬 Push notification received:', notification);
      
      const data = asNotificationPayload(notification.data || notification.notification?.data);

      if (data.type === 'mfa_approval_request') {
        console.log('[DeviceService] 🔐 MFA approval request received:', data.challenge_id);
        this.persistMfaApprovalPrompt(data, {
          title: notification.title,
          body: notification.body,
          source: 'push_notification_received',
        });
        window.dispatchEvent(new CustomEvent('mfaApprovalRequest', { detail: data }));

        // pushNotificationReceived only fires in the foreground — popup
        // immediately. hasFocus() is unreliable inside the WebView (often
        // false while visible), which previously let the prompt fall through
        // to the tray even with the app open.
        if (typeof document === 'undefined' || !document.hidden) {
          this.notifyHandlers({
            title: notification.title,
            body: notification.body,
            data,
          });
          return;
        }
      }
      
      // Check if this is a dismiss notification command from server
      if (data.type === 'dismiss_notification' && data.thread_id) {
        console.log('[DeviceService] 🔕 Received dismiss command for thread:', data.thread_id);
        await this.cancelNotificationForThread(data.thread_id);
        return; // Don't show anything for dismiss commands
      }
      
      // Handle call notification dismissal
      if (data.type === 'dismiss_call' && data.call_id) {
        console.log('[DeviceService] 🔕 Received dismiss call command:', data.call_id);
        await this.cancelNotificationForThread(`call_${data.call_id}`);
        // Dispatch event so UI can close any open call modals
        window.dispatchEvent(new CustomEvent('callDismissed', { detail: data }));
        return; // Don't show anything for dismiss commands
      }

      // Handle email notification dismissal
      // Fired when the email is marked as read on any surface (web portal, desktop, another device)
      if (data.type === 'dismiss_email_notification' && data.folder) {
        console.log('[DeviceService] 📧🔕 Received dismiss email notification command, folder:', data.folder);
        await this.cancelNotificationForThread(`email_${data.folder}`);
        // Dispatch event so email store / badge can update
        window.dispatchEvent(new CustomEvent('emailNotificationDismissed', { detail: { folder: data.folder } }));
        return; // Don't show anything for dismiss commands
      }

      // ── Phase 13: Session control commands (data-only FCM pushes) ──
      // The Go WebSocket service sends these via SendDataOnly() when an admin
      // triggers sign-out-everywhere, lock-now, or disables a user. They are
      // data-only pushes (no notification title/body), so we handle them
      // silently — no local notification is shown.
      // §29: the FCM payload may arrive either as type=session_terminated
      // (legacy) or type=session_control + command=<cmd>; both are accepted.
      // install_id targets a specific install — other installs ignore it.
      const sessionCommand = data.type === 'session_control' ? data.command : data.type;
      if (data.install_id) {
        const localInstallId = this.getDeviceId() || this.getAppId();
        if (localInstallId && data.install_id !== localInstallId) {
          return; // Command targets a different install on the same user
        }
      }
      if (sessionCommand === 'session_terminated' || sessionCommand === 'account_disabled') {
        // Policy gate: mobile_session_terminate_on_disable (client-scoped
        // device_policies). Server-side revocation is the authoritative
        // boundary either way — this only controls the local UX response.
        {
          const { authService } = await import('./authService');
          const enabled = authService.getUser()?.security_policy?.sessionPolicies?.mobileSessionTerminateOnDisable;
          if (enabled === false) {
            console.log('[DeviceService] Termination push ignored — mobile_session_terminate_on_disable policy is off');
            return;
          }
        }
        console.log('[DeviceService] 🔒 Session terminated by server:', data.reason, 'client_id:', data.client_id);
        // Clear auth state and offline data. If client_id is present, clear
        // only that client's offline data (the user may have other active
        // memberships). If client_id is absent or this is account_disabled,
        // clear ALL offline data across all clients.
        try {
          const { authService } = await import('./authService');
          const { default: offlineAuthService } = await import('./offlineAuthService');
          const { clearAllOfflineDataForClient, clearAllOfflineData } = await import('./offline/clearClientData');

          const clientId = typeof data.client_id === 'number'
            ? data.client_id
            : typeof data.client_id === 'string' ? Number(data.client_id) : null;

          if (clientId && sessionCommand === 'session_terminated') {
            // Per-client termination: clear only this client's offline data
            console.log('[DeviceService] 🧹 Clearing offline data for client_id:', clientId);
            await clearAllOfflineDataForClient(clientId).catch((err) => {
              console.warn('[DeviceService] Failed to clear per-client offline data:', err);
            });
            // §29: if the terminated client isn't this session's active
            // client, the session stays valid — wipe its data but keep the
            // user signed in (only that client's sessions were revoked).
            if (authService.getSessionClientId() !== clientId) {
              console.log('[DeviceService] Terminated client is not the active session client — staying signed in');
              return;
            }
          } else {
            // Full termination or account_disabled: clear ALL offline data
            console.log('[DeviceService] 🧹 Clearing ALL offline data (full termination)');
            await clearAllOfflineData().catch((err) => {
              console.warn('[DeviceService] Failed to clear all offline data:', err);
            });
          }

          authService.clearAuth();
          // Also clear offline credentials so the user can't bypass via
          // offline login after being disabled.
          await offlineAuthService.clearAllCredentials().catch(() => {});
        } catch (err) {
          console.warn('[DeviceService] Failed to clear auth on session_terminated:', err);
        }
        // Dispatch event so SessionProvider/ClientLayout redirects to login
        window.dispatchEvent(new CustomEvent('sessionTerminated', {
          detail: {
            reason: data.reason || (sessionCommand === 'account_disabled' ? 'account_disabled' : 'session_terminated'),
            client_id: data.client_id,
          },
        }));
        return; // Don't show a notification — the app handles the UI
      }

      if (sessionCommand === 'device_wipe' || data.type === 'device_wipe') {
        // Full device wipe — used when a company phone is reassigned to a
        // new employee. Clears ALL offline data, auth credentials, FCM token,
        // and Capacitor Preferences. This is destructive — the app resets
        // to a clean login screen.
        console.log('[DeviceService] 🧹 Device wipe command received:', data.reason);
        try {
          const { authService } = await import('./authService');
          const { default: offlineAuthService } = await import('./offlineAuthService');
          const { clearAllOfflineData } = await import('./offline/clearClientData');

          await clearAllOfflineData().catch((err) => {
            console.warn('[DeviceService] Failed to clear offline data on device_wipe:', err);
          });
          authService.clearAuth();
          await offlineAuthService.clearAllCredentials().catch(() => {});

          // Unregister FCM push token so pushes for the old user stop
          await this.unregisterPushToken().catch((err) => {
            console.warn('[DeviceService] Failed to unregister push token on wipe:', err);
          });
        } catch (err) {
          console.warn('[DeviceService] Failed to clear data on device_wipe:', err);
        }
        // Dispatch event so SessionProvider/ClientLayout redirects to login
        window.dispatchEvent(new CustomEvent('sessionTerminated', {
          detail: { reason: 'device_wipe' },
        }));
        return;
      }

      if (data.type === 'remote_lock') {
        // Policy gate: mobile_remote_lock_enabled (client-scoped
        // device_policies). Absent/false skips the lock-screen engage.
        {
          const { authService } = await import('./authService');
          const enabled = authService.getUser()?.security_policy?.sessionPolicies?.mobileRemoteLockEnabled;
          if (enabled === false) {
            console.log('[DeviceService] Remote lock ignored — mobile_remote_lock_enabled policy is off');
            return;
          }
        }
        console.log('[DeviceService] 🔒 Remote lock command received:', data.reason);
        // Dispatch event so BiometricLockScreen engages the lock
        window.dispatchEvent(new CustomEvent('remoteLock', {
          detail: { reason: data.reason || 'admin_lock' },
        }));
        return; // Don't show a notification — the lock screen is the UI
      }

      // ── Admin location wake-up request ──
      // Sent as a visible FCM when an admin escalates an on-demand location
      // request (the device wasn't reachable via WebSocket). We auto-capture
      // GPS via the 'locationCaptureRequested' window event — OperationsContext
      // listens for it and submits the position — and also persist the request
      // so a cold-started app captures once mounted. The notification itself
      // still displays (fall through — this is the explicit escalation path).
      if (data.type === 'location_capture_requested') {
        console.log('[DeviceService] 📍 Location capture request push received:', data.request_id);
        this.persistLocationCaptureRequest(data);
        window.dispatchEvent(new CustomEvent('locationCaptureRequested', { detail: data }));
      }

      // ── Phase 10.3: Remote support consent request ──
      // Sent as a visible FCM (type=support_session_request) when a
      // technician requests a browser screen-share session. The in-app
      // consent dialog (notify.confirm via RemoteSupportReceiver) is
      // driven by this event — the FCM notification covers the
      // backgrounded/killed state.
      if (data.type === 'support_session_request') {
        console.log('[DeviceService] 🖥️ Remote support request received:', data.session_id);
        window.dispatchEvent(new CustomEvent('supportSessionRequest', {
          detail: {
            session_id: data.session_id,
            room_name: data.room_name,
            technician_name: data.technician_name,
          },
        }));
        this.notifyHandlers({
          title: notification.title,
          body: notification.body,
          data,
        });
        return;
      }

      // Support session ended/rejected — data-only FCM so an in-progress
      // share or open consent prompt can be torn down silently.
      if (data.type === 'session_control' &&
          (data.command === 'support_session_ended' || data.command === 'support_session_rejected')) {
        console.log('[DeviceService] 🖥️ Support session ended remotely:', data.session_id);
        window.dispatchEvent(new CustomEvent('supportSessionEnded', {
          detail: {
            session_id: data.session_id,
            command: data.command,
            reason: data.reason,
          },
        }));
        return;
      }

      // Handle incoming voice calls - show high-priority notification
      // Note: On Android, the native TotalAccessMessagingService handles this with fullscreen intent
      // This is a fallback for when app is in foreground or for iOS
      if (data.type === 'incoming_call' || data.type === 'operations_incoming_call') {
        // If the app has focus, the WebSocket-driven in-app call UI is already
        // showing — skip the local notification to avoid stacking on top of it.
        if (typeof document !== 'undefined' && document.hasFocus() && !document.hidden) {
          console.log('[DeviceService] 📞🔇 App has focus — skipping call notification, in-app UI handles it');
          window.dispatchEvent(new CustomEvent('incomingCall', { detail: data }));
          this.notifyHandlers({
            title: notification.title,
            body: notification.body,
            data: { ...data, type: 'incoming_call' }
          });
          return;
        }

        console.log('[DeviceService] 📞 Incoming voice call:', data);
        try {
          const { LocalNotifications } = await import('@capacitor/local-notifications');
          const notificationId = this.generateNotificationId(`call_${data.call_id}`);
          const isSipCall = data.is_sip_call === true || data.is_sip_call === 'true' || data.call_type === 'sip_inbound';
          const flatTrunkName = typeof data.sip_trunk_name === 'string' ? data.sip_trunk_name.trim() : '';
          const flatDidNumber = typeof data.sip_did_number === 'string' ? data.sip_did_number.trim() : '';
          const nestedSipData = data.sip_data && typeof data.sip_data === 'object' ? data.sip_data : {};
          const trunkName = flatTrunkName || (typeof nestedSipData.trunk_name === 'string' ? nestedSipData.trunk_name.trim() : '');
          const didNumber = flatDidNumber || (typeof nestedSipData.did_number === 'string' ? nestedSipData.did_number.trim() : '');
          const callTitle = isSipCall
            ? `TotalAccess PBX • ${data.caller_name || 'Incoming Call'}`
            : `TotalAccess Call • ${data.caller_name || 'Incoming Call'}`;
          const callBody = isSipCall
            ? `Incoming SIP call${trunkName ? ` on ${trunkName}` : ''}${didNumber ? ` • DID ${didNumber}` : ''}`
            : `Incoming ${data.call_type === 'video' ? 'video' : 'voice'} call — Tap to answer`;
          
          // Create the "calls" channel if it doesn't exist (Android 8.0+)
          try {
            await LocalNotifications.createChannel({
              id: 'totalaccess_calls',
              name: 'Incoming Calls',
              description: 'Voice and video calls',
              importance: 5, // IMPORTANCE_HIGH
              visibility: 1, // VISIBILITY_PUBLIC
              sound: 'ringtone',
              vibration: true,
              lights: true
            });
          } catch (_error) {
            // Channel may already exist
          }
          
          await LocalNotifications.schedule({
            notifications: [
              {
                title: callTitle,
                body: callBody,
                id: notificationId,
                schedule: { at: new Date(Date.now() + 100) },
                sound: 'ringtone', // Use ringtone sound
                channelId: 'totalaccess_calls', // High priority channel for calls
                smallIcon: 'ic_stat_icon_config_sample',
                largeIcon: 'ic_launcher',
                extra: { ...data, notification_id: notificationId, type: 'incoming_call' },
                ongoing: true, // Can't be swiped away
                autoCancel: false
              }
            ]
          });
          
          // Also dispatch custom event for the app to handle (opens call UI)
          window.dispatchEvent(new CustomEvent('incomingCall', { detail: data }));
        } catch (error) {
          console.error('[DeviceService] Failed to show call notification:', error);
          // Still dispatch the event even if notification fails
          window.dispatchEvent(new CustomEvent('incomingCall', { detail: data }));
        }
        
        this.notifyHandlers({
          title: notification.title,
          body: notification.body,
          data: { ...data, type: 'incoming_call' }
        });
        return;
      }
      
      // Skip local notification if the app actually has focus — the in-app UI
      // (WebSocket-driven) already handles messages when the user is actively using the app.
      // Only show local notifications when the app is technically "foreground" but the
      // webview is hidden (e.g. picture-in-picture, split screen, or briefly backgrounded).
      if (typeof document !== 'undefined' && document.hasFocus() && !document.hidden) {
        console.log('[DeviceService] 🔇 App has focus — skipping local notification, in-app UI handles it');
        
        // For new emails, dispatch event so EnhancedEmailModule refreshes the thread list.
        // The WebSocket may be slow or disconnected on Capacitor, so FCM is a reliable
        // backup signal that new mail has arrived.
        if (data.type === 'new_email') {
          window.dispatchEvent(new CustomEvent('emailNewEmailReceived', {
            detail: { messageId: data.message_id, folder: data.folder || 'INBOX' }
          }));
        }
        
        this.notifyHandlers({
          title: notification.title,
          body: notification.body,
          data: notification.data
        });
        return;
      }

      // Show local notification since Android doesn't show push notifications when app is in foreground
      try {
        const { LocalNotifications } = await import('@capacitor/local-notifications');
        
        // Check permission
        const permResult = await LocalNotifications.checkPermissions();
        if (permResult.display !== 'granted') {
          await LocalNotifications.requestPermissions();
        }
        
        // Generate a consistent notification ID from thread_id so we can cancel it later
        // Use a hash of the thread_id to get a stable integer
        const threadId = data.thread_id || 'general';
        const notificationId = this.generateNotificationId(threadId);
        
        // Track this notification ID so we can cancel it when messages are read
        this.activeNotificationIds.set(threadId, notificationId);
        
        // Schedule immediate local notification to show the push content
        await LocalNotifications.schedule({
          notifications: [
            {
              title: notification.title || 'TotalAccess',
              body: notification.body || '',
              id: notificationId, // Consistent ID per thread - new messages replace old
              schedule: { at: new Date(Date.now() + 100) }, // Show immediately
              sound: 'default',
              smallIcon: 'ic_stat_icon_config_sample',
              largeIcon: 'ic_launcher',
              extra: { ...data, notification_id: notificationId }
            }
          ]
        });
        console.log(`[DeviceService] 📢 Local notification shown (id: ${notificationId}, thread: ${threadId})`);
      } catch (error) {
        console.error('[DeviceService] Failed to show local notification:', error);
      }
      
      // For new emails in the non-focused foreground path, also trigger a thread refresh
      // so that the thread list is already up-to-date when the user switches back.
      if (data.type === 'new_email') {
        window.dispatchEvent(new CustomEvent('emailNewEmailReceived', {
          detail: { messageId: data.message_id, folder: data.folder || 'INBOX' }
        }));
      }
      
      this.notifyHandlers({
        title: notification.title,
        body: notification.body,
        data: notification.data
      });
    });

    // Notification action performed (user tapped notification)
    pushNotifications.addListener('pushNotificationActionPerformed', (notification: NativePushActionPerformed) => {
      console.log('[DeviceService] 👆 Push notification tapped:', notification);
      
      const data = asNotificationPayload(notification.notification?.data);

      // Remote support consent request tapped — surface the in-app
      // consent dialog now that the app is in the foreground.
      if (data.type === 'support_session_request') {
        console.log('[DeviceService] 🖥️ Support request notification tapped:', data.session_id);
        window.dispatchEvent(new CustomEvent('supportSessionRequest', {
          detail: {
            session_id: data.session_id,
            room_name: data.room_name,
            technician_name: data.technician_name,
          },
        }));
        this.notifyHandlers({
          title: notification.notification?.title,
          body: notification.notification?.body,
          data,
        });
        return;
      }

      // Admin location wake-up request tapped — persist so a cold-started
      // app captures GPS once OperationsContext mounts, and dispatch
      // immediately for warm starts. No worker interaction required.
      if (data.type === 'location_capture_requested') {
        console.log('[DeviceService] 📍 Location capture request tapped:', data.request_id);
        this.persistLocationCaptureRequest(data);
        window.dispatchEvent(new CustomEvent('locationCaptureRequested', { detail: data }));
        this.notifyHandlers({
          title: notification.notification?.title,
          body: notification.notification?.body,
          data,
        });
        return;
      }

      if (data.type === 'mfa_approval_request' || data.action === 'open_mfa_approval') {
        const approvalUrl = this.persistMfaApprovalPrompt(data, {
          title: notification.notification?.title,
          body: notification.notification?.body,
          source: 'push_notification_action',
        });
        console.log('[DeviceService] 🔐 MFA approval notification tapped:', {
          challengeId: data.challenge_id,
          approvalUrl,
        });

        if (window.__coldStartLog) {
          window.__coldStartLog('MFA_PUSH', 'Stored MFA approval payload from push tap', {
            challengeId: data.challenge_id,
            hasApprovalUrl: !!approvalUrl,
            purpose: data.purpose || null,
          });
        }

        window.dispatchEvent(new CustomEvent('mfaApprovalRequest', { detail: data }));

        this.routeMfaApprovalPrompt(approvalUrl);

        this.notifyHandlers({
          title: notification.notification?.title,
          body: notification.notification?.body,
          data,
        });
        return;
      }
      
      // Handle incoming call tap - dispatch event (both messaging and operations calls)
      if (data.type === 'incoming_call' || data.type === 'operations_incoming_call') {
        console.log('[DeviceService] 📞 Call notification tapped:', data);
        window.dispatchEvent(new CustomEvent('incomingCallTapped', { detail: data }));
      }

      if (data.type === 'appointment_created' || data.action === 'open_appointment') {
        const appointmentId = data.appointment_id;
        const parentJobId = data.parent_job_id;
        console.log('[DeviceService] 📅 Appointment notification tapped:', { appointmentId, parentJobId, data });

        if (appointmentId) {
          this.persistAppointmentNavigation({ appointmentId, parentJobId });

          const targetPath = `/client-zone/dashboard?appointment_id=${encodeURIComponent(appointmentId)}${parentJobId ? `&parent_job_id=${encodeURIComponent(parentJobId)}` : ''}`;

          setTimeout(() => {
            window.location.href = targetPath;
          }, 100);
        }

        this.notifyHandlers({
          title: notification.notification?.title,
          body: notification.notification?.body,
          data: notification.notification?.data,
        });
        return;
      }
      
      // Handle message notification tap - navigate to message-centre
      if (data.type === 'thread_message' || data.action === 'open_messages') {
        const threadId = data.thread_id;
        const threadName = data.thread_name || data.sender_name || 'Message';
        console.log('[DeviceService] 📬 Message notification tapped, thread_id:', threadId);
        
        // Store the pending navigation in sessionStorage so it persists through app startup
        if (threadId) {
          try {
            sessionStorage.setItem('pending_message_navigation', JSON.stringify({
              threadId,
              threadName,
              timestamp: Date.now()
            }));
            console.log('[DeviceService] 📬 Stored pending message navigation for thread:', threadId);
            
            // Also try to show the fast-track loading UI if the startup script is available
            if (typeof window !== 'undefined' && typeof window.checkForPendingNavigation === 'function') {
              window.checkForPendingNavigation();
            }
          } catch (e) {
            console.error('[DeviceService] Failed to store pending navigation:', e);
          }
        }
        
        // Dispatch event for immediate handling if app is already loaded
        window.dispatchEvent(new CustomEvent('deepLinkMessage', {
          detail: { threadId, url: `totalaccess://client-zone/message-centre?thread_id=${threadId}` }
        }));
        
        // Open messages modal via events (works on both Capacitor and web)
        if (threadId) {
          console.log('[DeviceService] 📬 Opening messages modal via events');
          setTimeout(() => {
            window.dispatchEvent(new CustomEvent('toggleModal', {
              detail: { modalName: 'messages' }
            }));
            setTimeout(() => {
              window.dispatchEvent(new CustomEvent('openMessagesModal', {
                detail: { threadId, threadName }
              }));
            }, 200);
          }, 100);
        }
      }
      
      // Handle task notification tap - open the managed tasks modal
      if (data.type === 'task_assigned' || data.type === 'task_updated' || 
          data.type === 'task_due_soon' || data.type === 'task_completed' ||
          data.type === 'task_comment' || data.action === 'open_tasks' ||
          (data.type && typeof data.type === 'string' && data.type.startsWith('task'))) {
        console.log('[DeviceService] 📋 Task notification tapped:', data);
        
        // Store pending navigation for cold start
        try {
          sessionStorage.setItem('pending_task_navigation', JSON.stringify({
            taskId: data.task_id,
            timestamp: Date.now()
          }));
        } catch (error) {
          console.error('[DeviceService] Failed to store pending task navigation:', error);
        }
        
        // Dispatch event to open the managed tasks modal immediately
        setTimeout(() => {
          window.dispatchEvent(new CustomEvent('toggleModal', { 
            detail: { modalName: 'tasks' } 
          }));
        }, 100);
      }
      
      // Handle calendar notification tap - join the meeting room directly for
      // video reminders, otherwise open CalendarModal.
      if (data.type === 'calendar_reminder' || data.action === 'view_event' || data.action === 'join_meeting') {
        console.log('[DeviceService] 📅 Calendar notification tapped:', data);
        const meetingUrl = data.meeting_url;
        if (data.action === 'join_meeting' && typeof meetingUrl === 'string' &&
            (meetingUrl.startsWith('/') || meetingUrl.startsWith('http'))) {
          setTimeout(() => {
            window.location.href = meetingUrl;
          }, 100);
        } else {
          setTimeout(() => {
            window.dispatchEvent(new CustomEvent('toggleModal', {
              detail: { modalName: 'calendar' }
            }));
          }, 100);
        }
      }

      // Handle logbook trip completed notification tap - navigate to digital logbook
      if (data.type === 'logbook_trip_completed' || data.action === 'enter_end_odometer' || data.action === 'view_trip') {
        const tripId = data.trip_id;
        const vehicleRegistration = data.vehicle_registration;
        const requiresOdometer = data.requires_odometer === true || data.requires_odometer === 'true';
        console.log('[DeviceService] 🚗 Logbook trip notification tapped:', { tripId, vehicleRegistration, requiresOdometer });

        try {
          sessionStorage.setItem('pending_logbook_navigation', JSON.stringify({
            tripId,
            vehicleRegistration,
            requiresOdometer,
            targetView: 'smartfield',
            smartfieldTab: 'logbook',
            timestamp: Date.now(),
          }));
          localStorage.setItem('deeplink_pending_logbook_navigation', JSON.stringify({
            tripId,
            vehicleRegistration,
            requiresOdometer,
            targetView: 'smartfield',
            smartfieldTab: 'logbook',
            timestamp: Date.now(),
          }));
        } catch (e) {
          console.error('[DeviceService] Failed to persist logbook deep link:', e);
        }

        sessionStorage.setItem('dashboard_pending_view', 'smartfield');

        window.dispatchEvent(new CustomEvent('deepLinkLogbook', {
          detail: { tripId, vehicleRegistration, requiresOdometer, targetView: 'smartfield', smartfieldTab: 'logbook' }
        }));

        const targetPath = '/client-zone/dashboard?view=smartfield&smartfield_tab=logbook';
        if (window.location.pathname !== targetPath) {
          window.location.href = targetPath;
        }
      }
      
      // Handle email notification tap - navigate to email-centre (deep link handler)
      if (data.type === 'new_email' || data.action === 'open_email') {
        const messageId = data.message_id;
        const threadId = data.thread_id || data.threadId;
        const folder = data.folder || 'INBOX';
        const accountId = data.account_id || data.accountId || data.mailbox_id || data.mailboxId;
        const clientId = data.client_id || data.clientId;
        const subject = data.subject || data.sender_name || data.from || 'Email';
        const senderName = data.sender_name || data.from || '';
        console.log('[DeviceService] 📧 Email notification tapped, message_id:', messageId, 'folder:', folder, 'account_id:', accountId);
        
        // Store the pending navigation in sessionStorage so it persists through app startup
        if (messageId) {
          try {
            sessionStorage.setItem('pending_email_navigation', JSON.stringify({
              emailId: messageId,
              messageId,
              threadId,
              folder,
              subject,
              senderName,
              accountId,
              clientId,
              timestamp: Date.now()
            }));
            console.log('[DeviceService] 📧 Stored pending email navigation for message:', messageId);
            
            // Also try to show the fast-track loading UI if the startup script is available
            if (typeof window !== 'undefined' && typeof window.checkForPendingNavigation === 'function') {
              window.checkForPendingNavigation();
            }
          } catch (error) {
            console.error('[DeviceService] Failed to store pending email navigation:', error);
          }
        }
        
        // Dispatch openSmartConnect event — the Header (always mounted via ConditionalLayout)
        // listens for this and opens the email modal directly.
        // This works for both warm start (app already loaded) and cold start:
        //   - Warm start: Header receives the event and opens the email modal immediately.
        //   - Cold start: The event may be lost, but capacitor-startup.js reads
        //     `pending_email_navigation` from sessionStorage, sets window.__pendingNavigation,
        //     and the Header picks it up on mount — no double navigation needed.
        setTimeout(() => {
          window.dispatchEvent(new CustomEvent('openSmartConnect', {
            detail: {
              tab: 'email',
              emailId: messageId,
              messageId,
              threadId,
              folder,
              accountId,
              clientId,
              subject,
            }
          }));
          // Also dispatch deepLinkEmail for notificationStateManager
          window.dispatchEvent(new CustomEvent('deepLinkEmail', {
            detail: { emailId: messageId, messageId, threadId, folder, accountId, clientId }
          }));
        }, 100);
      }

      // Resident "My Visitors" notification tap — visitor arrival, approval
      // request or booking lifecycle. Deep link opens the my-visitors view on
      // the dashboard; approve_visitor goes straight to the requests sub-view.
      if (typeof data.type === 'string' && data.type.startsWith('visitor_')) {
        console.log('[DeviceService] 🚪 Visitor notification tapped:', data);

        try {
          sessionStorage.setItem('dashboard_pending_view', 'my-visitors');
          if (data.action === 'approve_visitor') {
            sessionStorage.setItem('myvisitors_pending_subview', 'requests');
          }
        } catch (e) {
          console.error('[DeviceService] Failed to persist visitor navigation:', e);
        }

        // Warm start on the dashboard: the event switches views in-place.
        window.dispatchEvent(new CustomEvent('deepLinkMyVisitors', {
          detail: { action: data.action || 'open', visitor_code_id: data.visitor_code_id }
        }));

        // Cold start / off-dashboard: navigate via the server-provided link.
        const onDashboard = window.location.pathname.endsWith('/client-zone/dashboard');
        if (!onDashboard) {
          const targetPath = typeof data.link === 'string' && data.link.startsWith('/client-zone')
            ? data.link
            : '/client-zone/dashboard?view=my-visitors';
          setTimeout(() => {
            window.location.href = targetPath;
          }, 100);
        }
      }

      this.notifyHandlers({
        title: notification.notification?.title,
        body: notification.notification?.body,
        data: notification.notification?.data
      });
    });

    this.pushListenersRegistered = true;
    console.log('[DeviceService] 🔔 Push notification listeners registered');
  }

  /**
   * Auto-register FCM token with server when available
   */
  private async autoRegisterFCMToken(): Promise<void> {
    if (!this.fcmToken) return;

    // Check if user is logged in - check auth_tokens JSON first
    let authToken: string | null = null;
    if (typeof localStorage !== 'undefined') {
      const tokensJson = localStorage.getItem('auth_tokens');
      if (tokensJson) {
        try {
          const tokens = JSON.parse(tokensJson);
          authToken = tokens.access_token;
        } catch (_error) {
          // Ignore parse errors
        }
      }
      if (!authToken) {
        authToken = localStorage.getItem('auth_token') || localStorage.getItem('accessToken');
      }
    }
    
    if (!authToken) {
      console.log('[DeviceService] No auth token, will register FCM after login');
      return;
    }

    // Get user ID from token or storage
    const userId = this.getCurrentUserId();
    if (!userId) {
      console.log('[DeviceService] No user ID, will register FCM after login');
      return;
    }

    await this.registerFCMTokenWithServer(userId);
  }

  /**
   * Register FCM token with the server
   * Should be called after successful login
   */
  async registerFCMTokenWithServer(userId: number): Promise<boolean> {
    // Check network state first - avoid spam when offline
    if (typeof navigator !== 'undefined' && !navigator.onLine) {
      console.log('[DeviceService] Offline - deferring FCM token registration');
      // Queue for later registration when online
      this.queueFCMRegistrationForOnline(userId);
      return false;
    }
    
    // Get token from cache or localStorage
    const token = this.fcmToken || 
      (typeof localStorage !== 'undefined' ? localStorage.getItem('capacitor_fcm_token') : null);

    if (!token) {
      console.log('[DeviceService] No FCM token available to register');
      // Try to request permissions and get token
      await this.requestPushNotificationPermission();
      return false;
    }

    try {
      // Get auth token - try auth_tokens JSON object first, then fallback
      let authToken: string | null = null;
      
      if (typeof localStorage !== 'undefined') {
        const tokensJson = localStorage.getItem('auth_tokens');
        if (tokensJson) {
          try {
            const tokens = JSON.parse(tokensJson);
            authToken = tokens.access_token;
          } catch (_error) {
            console.warn('[DeviceService] Failed to parse auth_tokens');
          }
        }
        // Fallback to legacy keys
        if (!authToken) {
          authToken = localStorage.getItem('auth_token') || localStorage.getItem('accessToken');
        }
      }

      if (!authToken) {
        console.warn('[DeviceService] No auth token for FCM registration');
        return false;
      }

      // Use relative URL for Capacitor compatibility - proxied through Next.js
      const fcmRegisterUrl = '/api/v1/messaging/fcm/register';
      
      console.log('[DeviceService] Registering FCM token via proxy:', fcmRegisterUrl);

      const response = await fetch(fcmRegisterUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify({
          user_id: userId,
          fcm_token: token,
          device_type: this.getPlatform(),
          app_version: process.env.NEXT_PUBLIC_APP_VERSION || '1.0.0',
          device_id: this.getDeviceId(),
          app_id: this.getAppId(),
          push_provider: this.getPushProvider()
        })
      });

      const rawBody = await response.text();
      let result: { success?: boolean; error?: string; message?: string } | null = null;
      if (rawBody) {
        try {
          result = JSON.parse(rawBody) as { success?: boolean; error?: string; message?: string };
        } catch (parseError) {
          console.warn('[DeviceService] Received non-JSON FCM register response:', rawBody);
        }
      }

      if (!response.ok) {
        console.error('[DeviceService] ❌ FCM registration failed:', response.status, result || rawBody);
        return false;
      }

      if (result?.success) {
        console.log('[DeviceService] ✅ FCM token registered with server');
        return true;
      }

      console.error('[DeviceService] ❌ Failed to register FCM token:', result?.error || rawBody || 'Unknown response');
      return false;
    } catch (error) {
      console.error('[DeviceService] ❌ Error registering FCM token:', error);
      return false;
    }
  }

  /**
   * Get current user ID from storage
   */
  private getCurrentUserId(): number | null {
    if (typeof localStorage === 'undefined') return null;

    // Try different storage keys - authService uses 'user_data'
    const userStr = localStorage.getItem('user_data') || 
                    localStorage.getItem('user') || 
                    localStorage.getItem('currentUser');
    if (userStr) {
      try {
        const user = JSON.parse(userStr);
        return user.id || user.userId || null;
      } catch {
        return null;
      }
    }

    // Try session storage
    const sessionUserStr = sessionStorage.getItem('user');
    if (sessionUserStr) {
      try {
        const user = JSON.parse(sessionUserStr);
        return user.id || user.userId || null;
      } catch {
        return null;
      }
    }

    return null;
  }

  /**
   * Queue FCM registration for when network comes back online
   */
  private queueFCMRegistrationForOnline(userId: number): void {
    // Store pending registration
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem('pending_fcm_registration_user', userId.toString());
    }
    
    // Set up one-time online listener
    const onlineHandler = () => {
      console.log('[DeviceService] Network restored - attempting queued FCM registration');
      const pendingUserId = localStorage.getItem('pending_fcm_registration_user');
      if (pendingUserId) {
        localStorage.removeItem('pending_fcm_registration_user');
        this.registerFCMTokenWithServer(parseInt(pendingUserId, 10));
      }
      window.removeEventListener('online', onlineHandler);
    };
    
    if (typeof window !== 'undefined') {
      window.addEventListener('online', onlineHandler);
    }
  }

  /**
   * Get current FCM token
   */
  getFCMToken(): string | null {
    return this.fcmToken || 
      (typeof localStorage !== 'undefined' ? localStorage.getItem('capacitor_fcm_token') : null);
  }

  /**
   * Subscribe to FCM token changes
   */
  onTokenChange(handler: (token: string | null) => void): () => void {
    this.tokenHandlers.add(handler);
    return () => this.tokenHandlers.delete(handler);
  }

  /**
   * Subscribe to push notification events
   */
  onNotification(handler: (notification: PushNotification) => void): () => void {
    this.notificationHandlers.add(handler);
    return () => this.notificationHandlers.delete(handler);
  }

  /**
   * Notify all handlers of a notification
   */
  private notifyHandlers(notification: PushNotification): void {
    this.notificationHandlers.forEach(handler => {
      try {
        handler(notification);
      } catch (error) {
        console.error('[DeviceService] Notification handler error:', error);
      }
    });
  }

  /**
   * Update mobile session with current client
   * Called when user switches clients
   */
  async updateSessionClient(userId: number, clientId: number): Promise<boolean> {
    try {
      const authToken = typeof localStorage !== 'undefined' 
        ? localStorage.getItem('auth_token') || localStorage.getItem('accessToken')
        : null;

      if (!authToken) {
        console.warn('[DeviceService] No auth token for session update');
        return false;
      }

      const appId = this.getAppId();
      if (!appId) {
        console.warn('[DeviceService] No app_id for session update');
        return false;
      }

      // Update session via Next.js API
      const response = await fetch('/api/v1/auth/update-session', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`,
          'X-App-Id': appId
        },
        body: JSON.stringify({
          user_id: userId,
          client_id: clientId,
          app_id: appId,
          device_id: this.getDeviceId()
        })
      });

      const result = await response.json();
      return result.success;
    } catch (error) {
      console.error('[DeviceService] Error updating session client:', error);
      return false;
    }
  }

  /**
   * Called on logout to cleanup
   */
  async cleanup(): Promise<void> {
    // Clear stored tokens
    this.setFcmToken(null);
    console.log('[DeviceService] Cleaned up');
  }

  /**
   * Unregister the FCM push token — clears the local token cache and
   * notifies the server so pushes for the old user stop being delivered
   * to this device after a company phone reassignment or account
   * disablement.
   *
   * The server-side session revocation (mobile_sessions revoked) is the
   * authoritative boundary — this prevents stale token reuse locally and
   * clears the server-side push_token so the Go FCM sender stops selecting
   * this device for future pushes.
   */
  async unregisterPushToken(): Promise<void> {
    const token = this.fcmToken;
    this.setFcmToken(null);
    console.log('[DeviceService] FCM push token unregistered (local cache cleared)');

    // Best-effort server-side unregister. The Go service clears
    // mobile_sessions.push_token for this user+app_id, so the FCM sender
    // stops selecting this device for future pushes.
    if (token) {
      try {
        const authToken = typeof localStorage !== 'undefined'
          ? (JSON.parse(localStorage.getItem('auth_tokens') || '{}')?.access_token
            || localStorage.getItem('auth_token')
            || localStorage.getItem('accessToken'))
          : null;
        if (!authToken) return;
        const appId = this.getAppId();
        await fetch('/api/v1/messaging/fcm/unregister', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authToken}`,
          },
          body: JSON.stringify({
            app_id: appId,
            device_id: this.getDeviceId(),
          }),
        }).catch((err) => {
          console.warn('[DeviceService] Failed to unregister FCM token with server:', err);
        });
      } catch {
        // Non-critical — the local cache is already cleared and the
        // server-side session revocation is the authoritative boundary.
      }
    }
  }

  /**
   * Update FCM token cache and notify subscribers
   */
  private setFcmToken(token: string | null): void {
    this.fcmToken = token;

    if (typeof localStorage !== 'undefined') {
      if (token) {
        localStorage.setItem('capacitor_fcm_token', token);
      } else {
        localStorage.removeItem('capacitor_fcm_token');
      }
    }

    this.tokenHandlers.forEach((handler) => {
      try {
        handler(token);
      } catch (error) {
        console.error('[DeviceService] Token handler error:', error);
      }
    });
  }

  /**
   * Get device info for API requests
   * Returns object to include in login/register requests
   */
  getDeviceInfoForAPI(): Record<string, string | boolean | null> {
    try {
      const platform = this.getPlatform();
      
      const deviceId = this.getDeviceId();
      return {
        app_id: this.getAppId() || (deviceId ? `capacitor_${platform}_${deviceId}` : `capacitor_${platform}`),
        device_id: deviceId || null,
        platform: platform,
        client_type: platform === 'web' ? 'web' : 'mobile',
        device_name: this.deviceInfo?.deviceName || `${platform} device`,
        device_model: this.deviceInfo?.model || null,
        device_manufacturer: this.deviceInfo?.manufacturer || null,
        os_version: this.deviceInfo?.osVersion || null,
        is_native_app: this.isNative(),
        fcm_token: this.getFCMToken() || null
      };
    } catch (error) {
      console.error('[DeviceService] Error in getDeviceInfoForAPI:', error);
      // Return minimal fallback - use stored values if available
      const storedAppId = typeof localStorage !== 'undefined' ? localStorage.getItem('capacitor_app_id') : null;
      return {
        app_id: storedAppId || `capacitor_fallback`,
        platform: 'unknown',
        client_type: 'mobile',
        is_native_app: true
      };
    }
  }

  /**
   * Generate a consistent notification ID from thread_id
   * Uses a simple hash to convert string thread_id to integer
   * Same thread will always get the same ID, so new messages replace old notifications
   */
  private generateNotificationId(threadId: string): number {
    let hash = 0;
    for (let i = 0; i < threadId.length; i++) {
      const char = threadId.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32-bit integer
    }
    // Ensure positive and within safe range for notification IDs
    return Math.abs(hash) % 2147483647;
  }

  /**
   * Cancel notification for a specific thread (called when messages are read)
   * This is the WhatsApp-style behavior - reading messages dismisses the notification
   * 
   * Uses the native NotificationManager plugin on Android which cancels FCM notifications
   * by calculating the same hash-based ID used when creating the notification
   */
  async cancelNotificationForThread(threadId: string): Promise<void> {
    if (!this.isNative()) return;
    
    try {
      // First, try using the native NotificationManager plugin (Android)
      // This cancels FCM push notifications that use thread_id hash
      try {
        const { NotificationManager } = await import('@/plugins/NotificationManager');
        console.log('[DeviceService] Attempting to cancel notification via NotificationManager plugin...');
        const result = await NotificationManager.cancelNotificationForThread({ threadId });
        console.log(`[DeviceService] 🔕 Cancelled FCM notification for thread ${threadId} via native plugin (ID: ${result.notificationId})`);
        return; // Success - don't need to try fallback
      } catch (nativeErr: unknown) {
        // Native plugin not available (iOS or plugin not loaded)
        const nativeMessage =
          typeof nativeErr === 'object' && nativeErr !== null && 'message' in nativeErr
            ? String(nativeErr.message)
            : String(nativeErr);
        console.log('[DeviceService] Native NotificationManager not available:', nativeMessage);
        console.log('[DeviceService] This is expected if app was built before plugin was added. Rebuild APK to fix.');
      }
      
      // Also try LocalNotifications as fallback for local notifications
      const { LocalNotifications } = await import('@capacitor/local-notifications');
      
      // Get the notification ID for this thread
      const notificationId = this.activeNotificationIds.get(threadId);
      
      if (notificationId) {
        await LocalNotifications.cancel({ notifications: [{ id: notificationId }] });
        this.activeNotificationIds.delete(threadId);
        console.log(`[DeviceService] 🔕 Cancelled local notification for thread ${threadId} (id: ${notificationId})`);
      } else {
        // Try cancelling with the hash-based ID even if not tracked
        const hashId = this.generateNotificationId(threadId);
        await LocalNotifications.cancel({ notifications: [{ id: hashId }] });
        console.log(`[DeviceService] 🔕 Cancelled local notification for thread ${threadId} (hash id: ${hashId})`);
      }
    } catch (error) {
      console.error('[DeviceService] Failed to cancel notification:', error);
    }
  }

  /**
   * Cancel all active notifications (e.g., on logout)
   */
  async cancelAllNotifications(): Promise<void> {
    if (!this.isNative()) return;
    
    try {
      const { LocalNotifications } = await import('@capacitor/local-notifications');
      
      const pending = await LocalNotifications.getPending();
      if (pending.notifications.length > 0) {
        await LocalNotifications.cancel({ notifications: pending.notifications });
        console.log(`[DeviceService] 🔕 Cancelled ${pending.notifications.length} pending notifications`);
      }
      
      this.activeNotificationIds.clear();
    } catch (error) {
      console.error('[DeviceService] Failed to cancel all notifications:', error);
    }
  }

  private deepLinkListenerRegistered = false;
  private deepLinkRetryCount = 0;
  private deepLinkMaxRetries = 5;
  private deepLinkRetryDelay = 500; // ms
  private meetingDeepLinkRetryCount = 0;
  private meetingDeepLinkMaxRetries = 20;

  /**
   * Check if app is ready to process deep links (auth available)
   */
  private isAppReadyForDeepLinks(): boolean {
    try {
      // Check if we have auth tokens (user is logged in)
      const hasAuth = !!(localStorage.getItem('auth_tokens') || localStorage.getItem('auth_token'));
      // Check if React/modals might be ready (startup overlay hidden)
      const startupOverlay = document.getElementById('capacitor-startup-overlay');
      const startupComplete = !startupOverlay || Boolean(startupOverlay.classList.contains('hiding'));
      return hasAuth && startupComplete;
    } catch {
      return false;
    }
  }

  /**
   * Whether the app shell has finished booting enough to accept a navigation.
   * Unlike isAppReadyForDeepLinks this does NOT require auth — guest meeting
   * join links must work for signed-out users too.
   */
  private isUiReadyForNavigation(): boolean {
    try {
      const startupOverlay = document.getElementById('capacitor-startup-overlay');
      return !startupOverlay || startupOverlay.classList.contains('hiding');
    } catch {
      return true;
    }
  }

  /**
   * Map a meeting deep link (https app link or totalaccess:// scheme) onto an
   * app-internal path. Same-origin relative paths keep the WebView on the
   * app-shell host so the existing session carries into the join flow.
   */
  private resolveMeetingDeepLinkPath(url: URL): string | null {
    const pathname = url.pathname || '';

    // https://<host>/meeting..., /meet/...
    if (pathname === '/meeting' || pathname.startsWith('/meeting/')) {
      return `${pathname}${url.search}${url.hash}`;
    }
    if (pathname === '/meet' || pathname.startsWith('/meet/')) {
      const seg = pathname.replace(/^\/meet\/?/, '');
      return seg ? `/meeting/${encodeURIComponent(seg)}${url.search}` : `/meeting${url.search}`;
    }

    // totalaccess://meeting/<rest>?… (host === 'meeting')
    if (url.host === 'meeting') {
      const rest = pathname.replace(/^\/+/, '');
      const base = rest ? `/meeting/${rest}` : '/meeting';
      return `${base}${url.search}${url.hash}`;
    }

    // totalaccess://meet/<id>?… (host === 'meet')
    if (url.host === 'meet') {
      const seg = pathname.replace(/^\/+/, '');
      return seg ? `/meeting/${encodeURIComponent(seg)}${url.search}` : `/meeting${url.search}`;
    }

    return null;
  }

  /**
   * Meeting-link hosts grouped by the database environment they serve. If a
   * link lands in an app edition pointing at a different environment (e.g. an
   * older dev build that still claims prod app links), the join page must be
   * loaded from the link's own origin — a relative navigation would hit the
   * wrong DB and report the invitation token as invalid/expired.
   */
  private static readonly PROD_MEETING_HOSTS = new Set([
    'totalaccess.co.za',
    'app.totalaccess.co.za',
    // Staging deployments share the production database.
    'staging.totalaccess.co.za',
    'staging-app.totalaccess.co.za',
  ]);

  private static readonly DEV_MEETING_HOSTS = new Set([
    'dev-next.totalaccess.co.za',
    'dev.totalaccess.co.za',
    'dev-app.totalaccess.co.za',
    'dev-staging.totalaccess.co.za',
  ]);

  private meetingHostEnv(host: string): 'prod' | 'dev' | null {
    if (CapacitorDeviceService.PROD_MEETING_HOSTS.has(host)) return 'prod';
    if (CapacitorDeviceService.DEV_MEETING_HOSTS.has(host)) return 'dev';
    return null;
  }

  /**
   * Retry loop for meeting deep links — gated on UI readiness (not auth) so
   * guest links work even when the user is signed out.
   */
  private scheduleMeetingDeepLinkRetry(eventUrl: string, source: string): void {
    if (this.meetingDeepLinkRetryCount >= this.meetingDeepLinkMaxRetries) {
      console.log('[DeviceService] 📹 Max retries reached for meeting deep link, giving up');
      this.meetingDeepLinkRetryCount = 0;
      return;
    }

    this.meetingDeepLinkRetryCount++;
    const delay = this.deepLinkRetryDelay * this.meetingDeepLinkRetryCount;

    setTimeout(() => {
      if (this.isUiReadyForNavigation()) {
        this.meetingDeepLinkRetryCount = 0;
        this.processDeepLink(eventUrl, source + '_retry');
      } else {
        this.scheduleMeetingDeepLinkRetry(eventUrl, source);
      }
    }, delay);
  }

  /**
   * Store a deep link persistently for recovery after cold start
   * Uses BOTH localStorage and sessionStorage for redundancy
   */
  private persistDeepLink(type: 'message' | 'email' | 'call' | 'smart-automation', data: GenericRecord): void {
    const key = `pending_${type}_navigation`;
    const payload = JSON.stringify({ ...data, timestamp: Date.now() });
    try {
      sessionStorage.setItem(key, payload);
      // Also store in localStorage as backup (survives app crashes)
      localStorage.setItem(`deeplink_${key}`, payload);
      console.log(`[DeviceService] 🔗 Persisted ${type} deep link:`, data);
    } catch (e) {
      console.error('[DeviceService] Failed to persist deep link:', e);
    }
  }

  private persistAppointmentNavigation(data: GenericRecord): void {
    const payload = JSON.stringify({ ...data, timestamp: Date.now() });
    try {
      sessionStorage.setItem('pending_appointment_navigation', payload);
      localStorage.setItem('deeplink_pending_appointment_navigation', payload);
      sessionStorage.setItem('dashboard_pending_view', 'smartfield');
      console.log('[DeviceService] 🔗 Persisted appointment deep link:', data);
    } catch (e) {
      console.error('[DeviceService] Failed to persist appointment deep link:', e);
    }
  }

  private openAttendanceInDashboard(options?: { source?: string }): void {
    try {
      sessionStorage.setItem('dashboard_pending_view', 'smartclock');
    } catch (storageError) {
      console.error('[DeviceService] Failed to persist SmartClock dashboard view:', storageError);
    }

    const currentPath = window.location.pathname;
    const isOnDashboardShell = currentPath.startsWith('/client-zone/dashboard');

    if (isOnDashboardShell) {
      console.log('[DeviceService] ⏱️ Opening SmartClock inside dashboard shell', options);
      window.dispatchEvent(new CustomEvent('capacitor-footer-nav', {
        detail: { view: 'smartclock' }
      }));
      return;
    }

    if (typeof window.__hideNavigationUI === 'function') {
      window.__hideNavigationUI();
    }

    console.log('[DeviceService] ⏱️ Navigating to dashboard SmartClock view', options);
    window.location.href = '/client-zone/dashboard';
  }

  /**
   * Persist a pending admin 'location_capture_requested' push so a
   * cold-started app captures GPS once OperationsContext mounts (the push
   * tap can launch the app before any React context exists). sessionStorage
   * covers same-session warm starts; the localStorage mirror survives
   * WebView storage clears.
   */
  private persistLocationCaptureRequest(data: NativePushNotificationPayload): void {
    try {
      const payload = JSON.stringify({ ...data, timestamp: Date.now() });
      sessionStorage.setItem('pending_location_capture', payload);
      localStorage.setItem('totalaccess_pending_location_capture', payload);
      console.log('[DeviceService] 📍 Persisted location capture request:', data.request_id);
    } catch (e) {
      console.error('[DeviceService] Failed to persist location capture request:', e);
    }
  }

  private persistMfaApprovalPrompt(
    data: NativePushNotificationPayload,
    options?: { title?: string; body?: string; source?: string }
  ): string | null {
    const purpose = typeof data.purpose === 'string' ? data.purpose : null;
    const approvalUrl = [data.approval_url, data.mobile_approval_url, data.approvalUrl].find(
      (value): value is string => typeof value === 'string' && value.length > 0,
    ) ?? null;
    const payload = JSON.stringify({
      type: 'mfa_approval_request',
      title: options?.title || data.title || (purpose === 'settings_enable_inapp' ? 'Approve in-app MFA' : 'Approve your sign-in'),
      body: options?.body || data.body || (purpose === 'settings_enable_inapp'
        ? 'Approve this request only if you just enabled in-app MFA in your TotalAccess security settings.'
        : 'Approve this sign-in only if you just tried to log in to TotalAccess.'),
      approvalToken: data.approval_token || data.approvalToken || null,
      approvalUrl,
      expiresAt: data.expires_at || data.expiresAt || null,
      purpose,
      challengeId: data.challenge_id || data.challengeId || null,
      source: options?.source || 'capacitor_device_service',
      timestamp: Date.now(),
    });

    try {
      sessionStorage.setItem('pending_mfa_approval', payload);
      localStorage.setItem('totalaccess_pending_mfa_approval', payload);
      console.log('[DeviceService] 🔐 Persisted MFA approval prompt:', {
        challengeId: data.challenge_id || data.challengeId,
        purpose,
        approvalUrl,
      });
    } catch (persistError) {
      console.error('[DeviceService] Failed to persist MFA approval payload:', persistError);
    }

    return approvalUrl;
  }

  private hasAuthenticatedSession(): boolean {
    try {
      return !!(localStorage.getItem('auth_tokens') || localStorage.getItem('auth_token'));
    } catch {
      return false;
    }
  }

  private routeMfaApprovalPrompt(approvalUrl: string | null): void {
    const hasAuth = this.hasAuthenticatedSession();
    const onClientZone = typeof window !== 'undefined' && (window.location.pathname.startsWith('/client-zone') || window.location.pathname === '/');

    if (hasAuth) {
      if (!onClientZone) {
        window.location.href = '/client-zone/dashboard';
      }
      return;
    }

    if (approvalUrl) {
      this.processDeepLink(approvalUrl, 'mfaApprovalRoute');
    } else {
      window.location.href = '/auth/mfa';
    }
  }

  /**
   * Clear persisted deep link after successful navigation
   */
  private clearPersistedDeepLink(type: 'message' | 'email' | 'call' | 'appointment' | 'smart-automation'): void {
    const key = `pending_${type}_navigation`;
    try {
      sessionStorage.removeItem(key);
      localStorage.removeItem(`deeplink_${key}`);
    } catch (_error) {}
  }

  /**
   * Process a deep link URL and dispatch appropriate events
   * Centralized handler for all deep link types
   * Includes retry logic for cold start scenarios
   */
  private processDeepLink(eventUrl: string, source: string = 'listener'): void {
    try {
      const url = new URL(eventUrl);
      const pathname = url.pathname || url.host + url.pathname; // Handle custom scheme
      
      console.log('[DeviceService] 🔗 Processing deep link from', source, ':', {
        protocol: url.protocol,
        host: url.host,
        pathname: pathname,
        search: url.search
      });

      // Handle public mobile handoff deep links before auth is available
      if (pathname.includes('auth/mobile-handoff') || (url.host === 'auth' && url.pathname.includes('mobile-handoff'))) {
        const targetPath = `/auth/mobile-handoff${url.search}${url.hash}`;
        console.log('[DeviceService] 🔐 Mobile handoff deep link detected, navigating to:', targetPath);

        if (window.location.pathname !== '/auth/mobile-handoff' || window.location.search !== url.search) {
          window.location.href = targetPath;
        }
        return;
      }

      if (pathname.includes('auth/mfa/approve') || (url.host === 'auth' && url.pathname.includes('/mfa/approve'))) {
        const targetPath = `/auth/mfa/approve${url.search}${url.hash}`;
        console.log('[DeviceService] 🔐 MFA approval deep link detected, navigating to:', targetPath);

        const hasAuth = this.hasAuthenticatedSession();

        try {
          const persisted = JSON.stringify({
            type: 'mfa_approval_request',
            approvalUrl: eventUrl,
            source,
            timestamp: Date.now(),
          });
          sessionStorage.setItem('pending_mfa_approval', persisted);
          localStorage.setItem('totalaccess_pending_mfa_approval', persisted);
          if (window.__coldStartLog) {
            window.__coldStartLog('MFA_DEEPLINK', 'Processing MFA approval deep link', { source, targetPath, hasAuth });
          }
        } catch (persistError) {
          console.error('[DeviceService] Failed to persist MFA approval deep link:', persistError);
        }

        if (hasAuth) {
          window.dispatchEvent(new CustomEvent('mfaApprovalRequest', {
            detail: {
              type: 'mfa_approval_request',
              approval_url: eventUrl,
              mobile_approval_url: eventUrl,
            },
          }));

          if (!window.location.pathname.startsWith('/client-zone') && window.location.pathname !== '/') {
            window.location.href = '/client-zone/dashboard';
          }
          return;
        }

        if (window.location.pathname !== '/auth/mfa/approve' || window.location.search !== url.search) {
          window.location.href = targetPath;
        }
        return;
      }

      // Mobile enrollment QR links — totalaccess://enroll (custom scheme,
      // in-app) or https://<portal>/enroll (verified app link). The payload
      // (company code, optional connect key/config name) lands before auth,
      // so it is parked in localStorage and consumed by the next login —
      // §34/§35: the install's devices row records enrollment attribution.
      if (url.host === 'enroll' || pathname === '/enroll' || url.host === 'enroll-device') {
        const pending = {
          code: (url.searchParams.get('c') || url.searchParams.get('code') || '').trim().toUpperCase(),
          key: url.searchParams.get('k') || undefined,
          config_name: url.searchParams.get('cfg') || undefined,
          server: url.protocol === 'https:' ? url.origin : (url.searchParams.get('server') || undefined),
          received_at: Date.now(),
        };

        console.log('[DeviceService] 📲 Enrollment deep link:', {
          code: pending.code ? '***' : null,
          config: pending.config_name || null,
          hasKey: !!pending.key,
          source,
        });

        try {
          storePendingEnrollment(pending);
        } catch (e) {
          console.warn('[DeviceService] Failed to persist enrollment prefill:', e);
        }

        if (this.hasAuthenticatedSession()) {
          // Already signed in — the next session heartbeat/install upsert
          // picks up attribution via device_info.enrollment. Surface a
          // confirmation for the UI layer and drop to the dashboard.
          window.dispatchEvent(new CustomEvent('deepLinkEnrollment', {
            detail: { code: pending.code, configName: pending.config_name, url: eventUrl },
          }));
          if (!window.location.pathname.startsWith('/client-zone')) {
            window.location.href = '/client-zone/dashboard';
          }
          return;
        }

        // Not signed in → directory sign-in with the company code prefilled.
        const signIn = `/auth/total-access-directory?onboarding_code=${encodeURIComponent(pending.code)}`;
        if (window.location.pathname !== '/auth/total-access-directory') {
          window.location.href = signIn;
        }
        return;
      }

      // Invitation app links — https://<portal>/invitation/{code}. Public
      // page (no auth needed): navigate the WebView straight to it so the
      // recipient sets their password inside the app.
      if (pathname.startsWith('/invitation/') || url.host === 'invitation') {
        const targetPath = `${url.pathname}${url.search}${url.hash}`;
        console.log('[DeviceService] 📨 Invitation deep link, navigating to:', targetPath);
        if (window.location.pathname !== url.pathname) {
          window.location.href = targetPath;
        }
        return;
      }

      // Handle message-centre deep links
      if (pathname.includes('message-centre') || pathname.includes('client-zone/message-centre')) {
        const threadId = url.searchParams.get('thread_id') || url.searchParams.get('threadId');
        console.log('[DeviceService] 📬 Message centre deep link, thread_id:', threadId);
        
        // ALWAYS persist for recovery (survives app restart)
        if (threadId) {
          this.persistDeepLink('message', { threadId });
        }
        
        // Check if app is ready to handle deep links
        if (!this.isAppReadyForDeepLinks()) {
          console.log('[DeviceService] 📬 App not ready, deep link persisted for later processing');
          // Schedule retry
          this.scheduleDeepLinkRetry(eventUrl, source);
          return;
        }
        
        // Dispatch event for the app to handle
        window.dispatchEvent(new CustomEvent('deepLinkMessage', {
          detail: {
            threadId: threadId,
            url: eventUrl
          }
        }));
        
        // Open messages modal via events (works on both Capacitor and web)
        if (threadId) {
          console.log('[DeviceService] 📬 Deep link: opening messages modal via events');
          window.dispatchEvent(new CustomEvent('toggleModal', {
            detail: { modalName: 'messages' }
          }));
          setTimeout(() => {
            window.dispatchEvent(new CustomEvent('openMessagesModal', {
              detail: { threadId }
            }));
          }, 200);
          this.clearPersistedDeepLink('message');
        }
        return;
      }
      
      // Handle email-centre deep links
      if (pathname.includes('email-centre') || pathname.includes('client-zone/email-centre')) {
        const emailId = url.searchParams.get('email_id') || url.searchParams.get('emailId');
        const folder = url.searchParams.get('folder') || 'INBOX';
        const threadId = url.searchParams.get('thread_id') || url.searchParams.get('threadId') || undefined;
        const accountId = url.searchParams.get('account_id') || url.searchParams.get('accountId') || url.searchParams.get('mailbox_id') || undefined;
        const clientId = url.searchParams.get('client_id') || url.searchParams.get('clientId') || undefined;
        console.log('[DeviceService] 📧 Email centre deep link, email_id:', emailId);
        
        // ALWAYS persist for recovery (survives app restart)
        if (emailId) {
          this.persistDeepLink('email', { messageId: emailId, threadId, folder, accountId, clientId });
        }
        
        // Check if app is ready to handle deep links
        if (!this.isAppReadyForDeepLinks()) {
          console.log('[DeviceService] 📧 App not ready, deep link persisted for later processing');
          this.scheduleDeepLinkRetry(eventUrl, source);
          return;
        }
        
        // Dispatch event
        window.dispatchEvent(new CustomEvent('deepLinkEmail', {
          detail: { emailId, messageId: emailId, threadId, folder, accountId, clientId, url: eventUrl }
        }));
        
        // Open email modal via events (works on both Capacitor and web)
        console.log('[DeviceService] 📧 Deep link: opening email modal via events');
        window.dispatchEvent(new CustomEvent('openSmartConnect', {
          detail: { tab: 'email', emailId, messageId: emailId, threadId, folder, accountId, clientId }
        }));
        this.clearPersistedDeepLink('email');
        return;
      }

      // Handle meeting deep links (guest invites + meeting entry)
      // Matches https://<host>/meeting…, /meet/… and totalaccess://meeting|meet/…
      if (
        pathname === '/meeting' || pathname.startsWith('/meeting/') ||
        pathname === '/meet' || pathname.startsWith('/meet/') ||
        url.host === 'meeting' || url.host === 'meet'
      ) {
        const targetPath = this.resolveMeetingDeepLinkPath(url);
        if (!targetPath) {
          console.log('[DeviceService] 📹 Meeting deep link had no usable path:', eventUrl);
          return;
        }

        // Cross-environment link: when the link's host belongs to a different
        // environment than this app shell, keep the link's origin so the join
        // page talks to the deployment that issued the invitation token.
        const linkEnv = url.protocol === 'https:' ? this.meetingHostEnv(url.host) : null;
        const shellEnv = this.meetingHostEnv(window.location.host);
        const destination = linkEnv && shellEnv && linkEnv !== shellEnv
          ? `${url.origin}${targetPath}`
          : targetPath;

        console.log('[DeviceService] 📹 Meeting deep link detected:', { targetPath: destination, source });

        // Persist so a cold-start crash can still replay the navigation.
        this.persistDeepLink('call', { meetingUrl: destination });

        // Guest links must work signed out — gate on UI readiness, not auth.
        if (!this.isUiReadyForNavigation()) {
          console.log('[DeviceService] 📹 App shell not ready, scheduling meeting deep link retry');
          this.scheduleMeetingDeepLinkRetry(eventUrl, source);
          return;
        }

        this.clearPersistedDeepLink('call');
        window.location.href = destination;
        return;
      }

      // Windows face-logon approval (§30): totalaccess://logon-approve?n=<nonce>
      // or https://<host>/auth/logon-approve?n=<nonce>. Requires a signed-in
      // session — the approve page re-checks auth itself.
      if (url.host === 'logon-approve' || pathname.startsWith('/auth/logon-approve')) {
        const nonce = url.searchParams.get('n') || url.searchParams.get('nonce') || '';
        if (!nonce) {
          console.log('[DeviceService] 🔐 Logon-approve deep link missing nonce:', eventUrl);
          return;
        }
        const target = `/auth/logon-approve?n=${encodeURIComponent(nonce)}`;
        console.log('[DeviceService] 🔐 Logon-approve deep link detected:', { source });
        if (!this.isUiReadyForNavigation()) {
          console.log('[DeviceService] 🔐 App shell not ready, retrying shortly');
          this.scheduleDeepLinkRetry(eventUrl, source);
          return;
        }
        window.location.href = target;
        return;
      }

      // Handle call deep links
      if (pathname.includes('/call') || pathname.includes('call')) {
        const callId = url.searchParams.get('call_id') || url.searchParams.get('callId');
        console.log('[DeviceService] 📞 Call deep link detected, call_id:', callId);
        
        // Dispatch event for call handler
        window.dispatchEvent(new CustomEvent('deepLinkCall', {
          detail: {
            callId,
            url: eventUrl
          }
        }));
        return;
      }

      const appointmentId = url.searchParams.get('appointment_id') || url.searchParams.get('appointmentId');
      if (appointmentId && (pathname.includes('client-zone/dashboard') || pathname.includes('/dashboard') || url.host === 'dashboard')) {
        const parentJobId = url.searchParams.get('parent_job_id') || url.searchParams.get('parentJobId');
        const targetPath = `/client-zone/dashboard?appointment_id=${encodeURIComponent(appointmentId)}${parentJobId ? `&parent_job_id=${encodeURIComponent(parentJobId)}` : ''}`;

        console.log('[DeviceService] 📅 Appointment deep link detected:', { appointmentId, parentJobId, targetPath });

        this.persistAppointmentNavigation({ appointmentId, parentJobId });

        if (!this.isAppReadyForDeepLinks()) {
          console.log('[DeviceService] 📅 App not ready, appointment deep link persisted for later processing');
          this.scheduleDeepLinkRetry(eventUrl, source);
          return;
        }

        window.location.href = targetPath;
        return;
      }

      const openTarget = url.searchParams.get('open');
      if (openTarget === 'attendance') {
        console.log('[DeviceService] ⏱️ Attendance open deep link detected');

        if (!this.isAppReadyForDeepLinks()) {
          console.log('[DeviceService] ⏱️ App not ready, scheduling attendance deep link retry');
          try {
            sessionStorage.setItem('dashboard_pending_view', 'smartclock');
          } catch (_storageError) {
            // Non-fatal if storage is unavailable
          }
          this.scheduleDeepLinkRetry(eventUrl, source);
          return;
        }

        this.openAttendanceInDashboard({ source });
        return;
      }
      
      // Handle sync-attendance deep links (triggered after biometric enrollment success)
      // URL format: totalaccess://sync-attendance?mode=personal|kiosk&employee=EMP001&action=enrollment_complete
      if (pathname.includes('sync-attendance') || url.host === 'sync-attendance') {
        const mode = url.searchParams.get('mode') || 'personal';
        const employee = url.searchParams.get('employee');
        const action = url.searchParams.get('action');
        
        console.log('[DeviceService] 🔄 Sync attendance deep link:', { mode, employee, action });
        
        // Dispatch event for attendance sync
        // SmartClock and SyncStatusContext listen for this
        window.dispatchEvent(new CustomEvent('deepLinkSyncAttendance', {
          detail: {
            mode,       // 'personal' or 'kiosk'
            employee,   // Employee number (e.g., 'EMP001')
            action,     // 'enrollment_complete'
            url: eventUrl
          }
        }));

        this.openAttendanceInDashboard({ source: `sync-attendance:${source}` });
        return;
      }
      
      // Handle sync-operations deep links (triggered after work item changes)
      // URL format: totalaccess://sync-operations?job_id=JOB001&action=work_item_updated
      if (pathname.includes('sync-operations') || url.host === 'sync-operations') {
        const jobId = url.searchParams.get('job_id');
        const action = url.searchParams.get('action');
        
        console.log('[DeviceService] 🔄 Sync operations deep link:', { jobId, action });
        
        // Dispatch event for operations sync
        // SmartTask listens for this
        window.dispatchEvent(new CustomEvent('deepLinkSyncOperations', {
          detail: {
            jobId,      // Job ID (e.g., 'JOB001')
            action,     // 'work_item_updated', 'job_completed', etc.
            url: eventUrl
          }
        }));
        
        // If the app is on operations, trigger a sync
        // Otherwise navigate to operations module
        if (window.location.pathname.includes('/total-operations')) {
          console.log('[DeviceService] 🔄 Already on operations, dispatching sync event only');
        } else {
          // Navigate to SmartTask to show sync status
          console.log('[DeviceService] 🔄 Navigating to operations module');
          window.location.href = '/client-zone/modules/total-operations?sync=true';
        }
        return;
      }
      
      // Handle sync-connect deep links (triggered after message/email changes)
      // URL format: totalaccess://sync-connect?module=messages|email&action=message_sent
      if (pathname.includes('sync-connect') || url.host === 'sync-connect') {
        const module = url.searchParams.get('module') || 'messages';
        const action = url.searchParams.get('action');
        
        console.log('[DeviceService] 🔄 Sync connect deep link:', { module, action });
        
        // Dispatch event for connect sync
        // MessagesModalNew and EnhancedEmailModule listen for this
        window.dispatchEvent(new CustomEvent('deepLinkSyncConnect', {
          detail: {
            module,     // 'messages' or 'email'
            action,     // 'message_sent', 'email_sent', etc.
            url: eventUrl
          }
        }));
        
        // Navigate if needed
        if (module === 'messages' && !window.location.pathname.includes('/total-connect')) {
          console.log('[DeviceService] 🔄 Navigating to messaging');
          // Open messages modal via event
          window.dispatchEvent(new CustomEvent('openMessagesModal'));
        } else if (module === 'email' && !window.location.pathname.includes('/total-connect')) {
          console.log('[DeviceService] 🔄 Navigating to email');
          // Open email modal via event
          window.dispatchEvent(new CustomEvent('openEmailModal'));
        }
        return;
      }
      
      // Handle smart-automation deep links (open Smart Automation module with auto-capture)
      // Widget/shortcut URLs emitted by iOS quick actions and WidgetKit links:
      //   totalaccess://nav?path=/client-zone/field&widget_action=begin
      //   totalaccess://smarttask?action=begin | smartclock?action=punch |
      //   capture-expense | dashboard
      // Normalize the host shortcuts into the nav form, then dispatch
      // ta-widget-navigate (same contract as the native warm-nav JS). Lean
      // pages and UnifiedDashboard listen for it; falls back to a hard
      // navigation when no listener consumed the event.
      const widgetHosts: Record<string, string> = {
        smarttask: '/client-zone/field?from_widget=true',
        field: '/client-zone/field?from_widget=true',
        tasks: '/client-zone/field?from_widget=true',
        smartclock: '/client-zone/smartclock?from_widget=true',
        clock: '/client-zone/smartclock?from_widget=true',
        'capture-expense': '/client-zone/automation?widget_action=capture_expense',
        automation: '/client-zone/automation',
        dashboard: '/client-zone/dashboard',
      };
      if (widgetHosts[url.host]) {
        const base = widgetHosts[url.host];
        const action = url.searchParams.get('action');
        const suffix = action && !base.includes('widget_action=')
          ? `&widget_action=${encodeURIComponent(action)}`
          : '';
        const navUrl = `totalaccess://nav?path=${encodeURIComponent(base + suffix)}`;
        this.processDeepLink(navUrl, source);
        return;
      }

      if (url.host === 'nav' || pathname === 'nav' || pathname === '/nav') {
        const targetPath = url.searchParams.get('path');
        if (!targetPath || !targetPath.startsWith('/')) {
          console.log('[DeviceService] 🔗 nav deep link missing path:', eventUrl);
          return;
        }
        if (!this.isAppReadyForDeepLinks()) {
          this.scheduleDeepLinkRetry(eventUrl, source);
          return;
        }
        const targetPathname = targetPath.split('?')[0];
        // widget_action may live inside the path's own query (nav?path=/x?widget_action=…)
        const embeddedParams = new URLSearchParams(
          targetPath.includes('?') ? targetPath.slice(targetPath.indexOf('?') + 1) : ''
        );
        const detail: Record<string, string> = { path: targetPath };
        if (targetPathname.includes('/client-zone/field')) detail.view = 'smartfield';
        if (targetPathname.includes('/client-zone/smartclock')) detail.view = 'smartclock';
        if (targetPathname.includes('/client-zone/automation')) detail.view = 'smart-automation';
        for (const key of ['widget_action', 'workflow', 'step']) {
          const value = url.searchParams.get(key) || embeddedParams.get(key);
          if (value) detail[key] = value;
        }
        window.dispatchEvent(new CustomEvent('ta-widget-navigate', { detail }));
        setTimeout(() => {
          if (window.location.pathname !== targetPathname) {
            window.location.href = targetPath;
          }
        }, 400);
        return;
      }

      // URL format: totalaccess://smart-automation?action=capture
      if (pathname.includes('smart-automation') || url.host === 'smart-automation') {
        const action = url.searchParams.get('action') || 'open';
        const normalizedAction = action === 'capture_expense' ? 'capture' : action;

        console.log('[DeviceService] 📸 Smart Automation deep link detected:', { action: normalizedAction });

        // Persist for cold start recovery
        this.persistDeepLink('smart-automation', { action: normalizedAction });

        // Check if app is ready
        if (!this.isAppReadyForDeepLinks()) {
          console.log('[DeviceService] 📸 App not ready, smart-automation deep link persisted for later processing');
          this.scheduleDeepLinkRetry(eventUrl, source);
          return;
        }

        // Dispatch event for UnifiedDashboard and SmartAutomation to handle
        window.dispatchEvent(new CustomEvent('deepLinkSmartAutomation', {
          detail: {
            action: normalizedAction,
            url: eventUrl
          }
        }));

        // Navigate to the lean automation route when neither it nor the
        // dashboard (which hosts the embedded view) is active.
        const currentPath = window.location.pathname;
        if (!currentPath.includes('/client-zone/automation') &&
            !currentPath.includes('/client-zone/dashboard')) {
          sessionStorage.setItem('pending_smart_automation_action', normalizedAction);
          window.location.href = normalizedAction === 'capture'
            ? '/client-zone/automation?widget_action=capture_expense'
            : '/client-zone/automation';
        }

        // Clear after successful dispatch
        this.clearPersistedDeepLink('smart-automation');
        return;
      }
      
      // Handle OAuth callbacks via the shared Capacitor OAuth handler.
      // This avoids losing cold-start callbacks that arrive before the login page listener is mounted.
      if (pathname.includes('oauth-callback')) {
        console.log('[DeviceService] 🔑 OAuth callback detected, handing off to capacitorOAuthHandler');
        void import('./capacitorOAuthHandler')
          .then(({ capacitorOAuthHandler }) => capacitorOAuthHandler.processOAuthCallback(eventUrl))
          .catch((oauthError) => {
            console.error('[DeviceService] Failed to hand off OAuth callback:', oauthError);
          });
        return;
      }
      
      console.log('[DeviceService] 🔗 Unhandled deep link type:', pathname);
      
    } catch (parseError) {
      console.error('[DeviceService] Failed to parse deep link URL:', parseError);
    }
  }

  /**
   * Check for and process any pending deep links that were captured before React was ready
   * This handles cold-start deep links that arrived before the app finished loading
   */
  async processPendingDeepLinks(): Promise<void> {
    if (!this.isNative()) return;
    
    // Check for deep link stored by capacitor-startup.js
    if (typeof window.__getPendingDeepLink === 'function') {
      const pendingLink = window.__getPendingDeepLink();
      if (pendingLink && pendingLink.url) {
        this.processDeepLink(pendingLink.url, 'startup_stored');
        
        // Clear after processing
        if (typeof window.__clearPendingDeepLink === 'function') {
          window.__clearPendingDeepLink();
        }
        return; // Only process one pending link at a time
      }
    }
    
    // Check for early deep links captured before listener was active
    if (window.__earlyDeepLinks && window.__earlyDeepLinks.length > 0) {
      const earlyLink = window.__earlyDeepLinks.shift();
      if (earlyLink && earlyLink.url) {
        this.processDeepLink(earlyLink.url, 'early_capture');
        return;
      }
    }
    
    // Check getLaunchUrl() for cold-start deep links
    try {
      const { App } = await import('@capacitor/app');
      const launchUrl = await App.getLaunchUrl();
      
      if (launchUrl && launchUrl.url) {
        // Only process if not already handled (check a flag)
        const processedKey = 'deeplink_processed_' + launchUrl.url.substring(0, 50);
        if (!sessionStorage.getItem(processedKey)) {
          sessionStorage.setItem(processedKey, 'true');
          // Clear this marker after 60 seconds to allow re-processing if needed
          setTimeout(() => sessionStorage.removeItem(processedKey), 60000);
          
          this.processDeepLink(launchUrl.url, 'getLaunchUrl');
        }
      }
    } catch (e) {}
  }

  /**
   * Schedule a retry for processing deep link after delay
   * Used when app is not yet ready during cold start
   */
  private scheduleDeepLinkRetry(eventUrl: string, source: string): void {
    if (this.deepLinkRetryCount >= this.deepLinkMaxRetries) {
      console.log('[DeviceService] 🔗 Max retries reached for deep link, giving up active retry');
      this.deepLinkRetryCount = 0;
      return;
    }
    
    this.deepLinkRetryCount++;
    const delay = this.deepLinkRetryDelay * this.deepLinkRetryCount; // Exponential backoff
    
    console.log(`[DeviceService] 🔗 Scheduling deep link retry ${this.deepLinkRetryCount}/${this.deepLinkMaxRetries} in ${delay}ms`);
    
    setTimeout(() => {
      if (this.isAppReadyForDeepLinks()) {
        console.log('[DeviceService] 🔗 App now ready, processing deep link');
        this.deepLinkRetryCount = 0;
        this.processDeepLink(eventUrl, source + '_retry');
      } else {
        this.scheduleDeepLinkRetry(eventUrl, source);
      }
    }, delay);
  }

  /**
   * Check and process any persisted deep links from localStorage
   * Called after auth is ready (e.g., from SessionProvider)
   */
  async processPersistedDeepLinks(): Promise<void> {
    if (!this.isNative()) return;
    
    const types = ['message', 'email', 'call', 'appointment', 'smart-automation'] as const;
    const now = Date.now();
    const maxAge = 60000; // 60 seconds
    
    for (const type of types) {
      // Check localStorage backup first
      const lsKey = `deeplink_pending_${type}_navigation`;
      const ssKey = `pending_${type}_navigation`;
      
      let data: PersistedDeepLinkPayload | null = null;
      
      // Try localStorage first (survives crashes)
      try {
        const lsData = localStorage.getItem(lsKey);
        if (lsData) {
          data = JSON.parse(lsData);
        }
      } catch {}
      
      // Fall back to sessionStorage
      if (!data) {
        try {
          const ssData = sessionStorage.getItem(ssKey);
          if (ssData) {
            data = JSON.parse(ssData);
          }
        } catch {}
      }
      
      if (data && data.timestamp && (now - data.timestamp) < maxAge) {
        console.log(`[DeviceService] 🔗 Found persisted ${type} deep link:`, data);
        
        if (type === 'message' && data.threadId) {
          // Clear first to avoid duplicate processing
          this.clearPersistedDeepLink('message');
          
          // Open the messages modal with the thread
          setTimeout(() => {
            // First, open the Messages modal
            window.dispatchEvent(new CustomEvent('toggleModal', { 
              detail: { modalName: 'messages' } 
            }));
            
            // Then, navigate to the specific thread
            setTimeout(() => {
              window.dispatchEvent(new CustomEvent('openMessagesModal', { 
                detail: { 
                  threadId: data.threadId,
                  threadName: data.threadName || 'Message'
                } 
              }));
            }, 300);
          }, 200);
          
          // Hide any startup overlays
          if (typeof window.__hideNavigationUI === 'function') {
            window.__hideNavigationUI();
          }
          
          return; // Process one at a time
        }
        
        if (type === 'email' && data.messageId) {
          this.clearPersistedDeepLink('email');

          const emailDetail = {
            emailId: data.messageId,
            messageId: data.messageId,
            threadId: data.threadId,
            folder: data.folder || 'INBOX',
            accountId: data.accountId,
            clientId: data.clientId,
          };

          // No navigation — /client-zone/email-centre does not exist, so every
          // hard navigation to it hits a 404. capacitor-startup.js then
          // re-stores pending_email_navigation from the URL and redirects to
          // the dashboard, where this persisted deep link fires again —
          // an infinite 404 ↔ dashboard loop. The Header listens for these
          // events and opens EmailMobileModal inline on any page.
          window.dispatchEvent(new CustomEvent('deepLinkEmail', {
            detail: emailDetail
          }));
          window.dispatchEvent(new CustomEvent('openSmartConnect', {
            detail: { tab: 'email', ...emailDetail }
          }));

          if (typeof window.__hideNavigationUI === 'function') {
            window.__hideNavigationUI();
          }

          return;
        }

        if (type === 'call' && data.meetingUrl) {
          this.clearPersistedDeepLink('call');

          if (typeof window.__hideNavigationUI === 'function') {
            window.__hideNavigationUI();
          }

          window.location.href = data.meetingUrl;
          return;
        }

        if (type === 'appointment' && data.appointmentId) {
          this.clearPersistedDeepLink('appointment');

          const targetPath = `/client-zone/dashboard?appointment_id=${encodeURIComponent(data.appointmentId)}${data.parentJobId ? `&parent_job_id=${encodeURIComponent(data.parentJobId)}` : ''}`;
          sessionStorage.setItem('dashboard_pending_view', 'smartfield');

          if (typeof window.__hideNavigationUI === 'function') {
            window.__hideNavigationUI();
          }

          window.location.href = targetPath;
          return;
        }

        if (type === 'smart-automation' && data.action) {
          this.clearPersistedDeepLink('smart-automation');

          sessionStorage.setItem('dashboard_pending_view', 'smart-automation');
          sessionStorage.setItem('pending_smart_automation_action', data.action);

          // Dispatch event for components that are already mounted
          window.dispatchEvent(new CustomEvent('deepLinkSmartAutomation', {
            detail: { action: data.action }
          }));

          if (!window.location.pathname.includes('/client-zone/dashboard')) {
            if (typeof window.__hideNavigationUI === 'function') {
              window.__hideNavigationUI();
            }
            window.location.href = '/client-zone/dashboard?view=smart-automation';
          }

          return;
        }
      } else if (data) {
        // Expired, clear it
        this.clearPersistedDeepLink(type);
      }
    }
  }

  /**
   * Initialize deep link listener for handling app URL opens
   * This handles deep links like:
   * - totalaccess://client-zone/message-centre?thread_id=xxx
   * - https://totalaccess.co.za/client-zone/message-centre?thread_id=xxx
   */
  async initDeepLinkListener(): Promise<void> {
    if (!this.isNative()) return;
    if (this.deepLinkListenerRegistered) return;

    try {
      const { App } = await import('@capacitor/app');
      
      // Mark that React listener is now active (so early listener can stop storing)
      window.__deepLinkListenerActive = true;
      
      // First, process any pending deep links that arrived before React was ready
      await this.processPendingDeepLinks();
      
      // Set up listener for future deep links
      await App.addListener('appUrlOpen', (event) => {
        this.processDeepLink(event.url, 'appUrlOpen');
      });
      
      this.deepLinkListenerRegistered = true;
      
    } catch (error) {
      console.error('[DeviceService] Failed to register deep link listener:', error);
    }
  }
}

// Extend Window interface for deep link handling
declare global {
  interface Window {
    __getPendingDeepLink?: () => { url: string; source: string; timestamp: number } | null;
    __clearPendingDeepLink?: () => void;
    __earlyDeepLinks?: Array<{ url: string }>;
    __deepLinkListenerActive?: boolean;
    __hideNavigationUI?: () => void;
    __hideCallConnectingUI?: () => void;
    __coldStartLog?: (scope: string, message: string, detail?: unknown) => void;
    checkForPendingNavigation?: () => void;
  }

  interface WindowEventMap {
    mfaApprovalRequest: CustomEvent<NativePushNotificationPayload>;
    supportSessionRequest: CustomEvent<{
      session_id?: string;
      room_name?: string;
      technician_name?: string;
    }>;
    supportSessionEnded: CustomEvent<{
      session_id?: string;
      command?: string;
      reason?: string;
    }>;
  }
}

// Export singleton instance
export const capacitorDeviceService = new CapacitorDeviceService();
export const deviceService = capacitorDeviceService;
export async function getCapacitorDeviceService(): Promise<CapacitorDeviceService | null> {
  return Capacitor.isNativePlatform() ? capacitorDeviceService : null;
}
export default capacitorDeviceService;
